﻿/**

 * @fileoverview Regression Pack Optimizer Page

 * @description Full-featured React port of the standalone Regression-Pack-Optimizer frontend.

 *              Upload ->' Configure ->' Pipeline Animation ->' Review (Accept/Reject) ->' Tabbed Results ->' Finalize & Download

 */



import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';

import { Tabs, Tab, Paper, Box } from '@mui/material';

import { regressionOptimizerApi } from '../services/regressionOptimizerApi';

import { ScheduleOptimization } from './ScheduleOptimization';

import { appInsights } from '@/telemetry';

import { useAuth } from '@/context';

import './RegressionOptimizer.css';



// --- Helpers ---



function fmtSize(b) {

  if (b < 1024) return b + ' B';

  if (b < 1048576) return (b / 1024).toFixed(1) + ' KB';

  return (b / 1048576).toFixed(1) + ' MB';

}



function esc(str) {

  if (str == null) return '';

  const d = document.createElement('div');

  d.textContent = String(str);

  return d.innerHTML;

}



const SAFE_URL_PROTOCOLS = new Set(['https:', 'http:', 'mailto:', 'tel:', '']);

function isSafeUrl(value) {
  try {
    const url = new URL(value, window.location.origin);
    return SAFE_URL_PROTOCOLS.has(url.protocol);
  } catch {
    return true; // relative URL or non-parseable — allow
  }
}

const URL_ATTRIBUTES = new Set(['href', 'src', 'action', 'data']);

function sanitizeFeedbackHtml(html) {

  if (!html) return '';

  const parser = new DOMParser();

  const doc = parser.parseFromString(html, 'text/html');

  doc.querySelectorAll('script,style').forEach(n => n.remove());

  doc.querySelectorAll('*').forEach((el) => {

    [...el.attributes].forEach((attr) => {

      const name = attr.name.toLowerCase();

      if (name.startsWith('on')) {
        el.removeAttribute(attr.name);
        return;
      }

      if (URL_ATTRIBUTES.has(name) && !isSafeUrl(attr.value || '')) {
        el.removeAttribute(attr.name);
      }

    });

  });

  return (doc.body.innerHTML || '').trim();

}



// --- Pipeline Stage Definitions ---



const PIPELINE_STAGES = [

  {

    key: 'ingest', label: 'Ingest & Encode', number: '1', statusKey: 'classical_dedup',

    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7,10 12,15 17,10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,

    tooltip: 'Parses the Excel file, detects column roles (ID, Title, Steps, Expected Result), groups multi-row test cases into logical units, and prepares text representations for AI analysis.',

  },

  {

    key: 'similarity', label: 'Semantic Similarity', number: '2', statusKey: 'cosine_similarity',

    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>,

    tooltip: 'Generates vector embeddings for each test case using AI, then computes semantic similarity between all pairs. Tests above 96% similarity are flagged as redundant duplicates; 92-96% are clubbing candidates.',

  },

  {

    key: 'intent', label: 'Intent Extraction', number: '3', statusKey: 'intent_extraction',

    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 2a10 10 0 1 0 10 10H12V2z"/><path d="M12 12 12 2a10 10 0 0 1 10 10z"/></svg>,

    tooltip: 'Uses LLM to extract the semantic intent of each test case: what Feature is being tested, what Action is performed, and what Assertion is validated. Groups tests by shared intent for parameterization analysis.',

  },

  {

    key: 'diffing', label: 'Step Diffing', number: '4', statusKey: 'step_diffing',

    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 3v18"/><path d="M3 12h18"/><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,

    tooltip: 'Performs structural comparison between test cases in each intent group using Longest Common Subsequence (LCS) algorithm. Identifies which steps are shared vs. divergent, and whether differences are value substitutions (parameterizable) or genuine structural gaps.',

  },

  {

    key: 'synthesis', label: 'Synthesis', number: '5', statusKey: 'synthesis',

    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 3v18"/><path d="M18.5 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z"/><path d="M5.5 21a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z"/><path d="M16 8l-4 4"/><path d="M8 16l4-4"/></svg>,

    tooltip: 'LLM synthesizes optimization recommendations: redundancy removal, parameterization (data-driven tests), or clubbing (sequential merging). Produces merged steps, parameter tables, and BDD scenarios for each recommendation.',

  },

];



const PROGRESS_STAGES = [

  { pct: 5, text: 'Stage 1 -- Ingesting & encoding test cases...', stage: 'ingest' },

  { pct: 20, text: 'Stage 2 -- Computing semantic similarity...', stage: 'similarity' },

  { pct: 40, text: 'Stage 3 -- Extracting intents via LLM...', stage: 'intent' },

  { pct: 60, text: 'Stage 4 -- Step-level structural diffing...', stage: 'diffing' },

  { pct: 80, text: 'Stage 5 -- Synthesizing recommendations...', stage: 'synthesis' },

  { pct: 95, text: 'Finalizing output...', stage: null },

];



// --- Toast Component ---



function Toast({ message, type, visible }) {

  return (

    <div className={`ro-toast ${type} ${visible ? 'visible' : ''}`}>

      {message}

    </div>

  );

}



// --- Main Component ---



export const RegressionOptimizer = () => {

  const { getUserProfile } = useAuth();

  // Health

  const [healthStatus, setHealthStatus] = useState(null);



  // File

  const [file, setFile] = useState(null);

  const [fileMetrics, setFileMetrics] = useState(null);

  const [dragging, setDragging] = useState(false);

  const fileInputRef = useRef(null);

  // Multi-suite support
  const [loadedSuites, setLoadedSuites] = useState([]); // [{ id, file, meta, metrics, source, name }]
  const [limitDialog, setLimitDialog] = useState(null); // { pendingSuite, currentTotal, newCount }
  const [thresholdOpen, setThresholdOpen] = useState(false);
  const MAX_TC_LIMIT = 500;
  const [expandedFolders, setExpandedFolders] = useState(new Set()); // paths of expanded tree nodes

  // ADO Import

  const [adoUrl, setAdoUrl] = useState('');

  const [adoImporting, setAdoImporting] = useState(false);

  const [adoError, setAdoError] = useState('');

  const [uploadMode, setUploadMode] = useState('ado'); // 'ado' | 'file'

  const [adoInputMode, setAdoInputMode] = useState('url'); // 'url' | 'fields'

  const [adoProject, setAdoProject] = useState('');

  const [adoPlanId, setAdoPlanId] = useState('');

  const [adoSuiteId, setAdoSuiteId] = useState('');



  // Config

  const [sheetName, setSheetName] = useState('');

  const [threshold, setThreshold] = useState(0.96);

  const [temperature, setTemperature] = useState(0.0);



  // Pipeline progress

  const [pipelineVisible, setPipelineVisible] = useState(false);

  const [pipelineStatus, setPipelineStatus] = useState('Initializing...');

  const [progressPct, setProgressPct] = useState(0);

  const [stageStates, setStageStates] = useState({});



  // Results

  const [resultData, setResultData] = useState(null);

  const [originalData, setOriginalData] = useState(null);

  const [sessionId, setSessionId] = useState(null);



  // Review

  const [reviewDecisions, setReviewDecisions] = useState({ removals: {}, recommendations: {} });

  const [reviewVisible, setReviewVisible] = useState(false);

  const [userFeedbackHtml, setUserFeedbackHtml] = useState('');

  const [userFeedbackText, setUserFeedbackText] = useState('');

  // Per-item reject feedback: { 'removal-<tcId>': 'text', 'rec-<idx>': 'text' }
  const [rejectFeedback, setRejectFeedback] = useState({});
  const [feedbackConfirmed, setFeedbackConfirmed] = useState({});

  // UI state

  const [isOptimizing, setIsOptimizing] = useState(false);

  const [activeTab, setActiveTab] = useState('diff');

  const [toast, setToast] = useState({ message: '', type: 'info', visible: false });

  // Domain classification
  const [domainClassification, setDomainClassification] = useState(null);
  const [selectedDomainAgent, setSelectedDomainAgent] = useState(null);
  const [trainingUploadDomain, setTrainingUploadDomain] = useState(null);
  const [customDomains, setCustomDomains] = useState([]);
  const [newDomainName, setNewDomainName] = useState('');
  const [skipTraining, setSkipTraining] = useState(false);
  const skipTrainingRef = useRef(false);
  const [sessionUploadedFiles, setSessionUploadedFiles] = useState([]);
  const [showConfirmStart, setShowConfirmStart] = useState(false);
  const pipelineDoneRef = useRef(false);

  const [expandedCols, setExpandedCols] = useState(new Set());

  const [activeBadge, setActiveBadge] = useState(null);

  const [expandedReviewItems, setExpandedReviewItems] = useState(new Set());

  const [columnWidths, setColumnWidths] = useState({});

  const feedbackEditorRef = useRef(null);



  const timerRef = useRef(null);

  const elapsedRef = useRef(null);

  const abortRef = useRef(null);

  const resizeStateRef = useRef(null);

  const [elapsedSec, setElapsedSec] = useState(0);

  const runStartRef = useRef(null);



  // Per-stage estimated durations (seconds) — scales with TC count
  const tcCount = fileMetrics?.testCases || 25;
  const scaleFactor = Math.max(1, tcCount / 25);

  const STAGE_EST_SEC = {
    ingest: Math.round(3 * scaleFactor),
    similarity: Math.round(8 * scaleFactor),
    intent: Math.round(15 * scaleFactor),
    diffing: Math.round(5 * scaleFactor),
    synthesis: Math.round(20 * scaleFactor),
    output: Math.round(5 * scaleFactor),
  };

  const TOTAL_EST_SEC = Object.values(STAGE_EST_SEC).reduce((a, b) => a + b, 0);



  // --- Health check on mount (retry with delay for token readiness) ---

  useEffect(() => {

    let cancelled = false;

    const checkHealth = (retries = 3) => {

      regressionOptimizerApi.health()

        .then(data => { if (!cancelled) setHealthStatus(data); })

        .catch(() => {

          if (!cancelled && retries > 0) {

            setTimeout(() => checkHealth(retries - 1), 1500);

          } else if (!cancelled) {

            setHealthStatus({ error: true });

          }

        });

    };

    // Small delay to let AuthContext provide the token

    setTimeout(() => checkHealth(), 500);

    return () => { cancelled = true; };

  }, []);



  // --- Toast helper ---

  const notify = useCallback((message, type = 'info') => {

    setToast({ message, type, visible: true });

    setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 4000);

  }, []);

  // --- Telemetry: track optimizer usage to Azure App Insights ---
  const telemetryFiredRef = useRef(new Set()); // prevent duplicate events per session

  const trackOptimizerEvent = useCallback((eventName, extraProps = {}) => {
    // Deduplicate: only fire each event once per optimizer session
    const dedupeKey = `${eventName}-${sessionId || 'no-session'}`;
    if (telemetryFiredRef.current.has(dedupeKey)) return;
    telemetryFiredRef.current.add(dedupeKey);

    const profile = getUserProfile?.() || {};
    const suiteNames = loadedSuites.map(s => s.name || s.file?.name || 'unknown').join(', ');
    const suiteSource = loadedSuites.length
      ? (loadedSuites[0].source === 'ado' ? 'ADO' : 'Excel Upload')
      : 'unknown';

    appInsights.trackEvent({
      name: eventName,
      properties: {
        source: 'RegressionOptimizer',
        userId: profile.email || profile.username || 'anonymous',
        userName: profile.name || 'anonymous',
        sessionId: sessionId || '',
        suiteNames,
        suiteSource,
        suiteCount: String(loadedSuites.length),
        totalTestCases: String(fileMetrics?.testCases || 0),
        totalSteps: String(fileMetrics?.totalSteps || 0),
        ...extraProps,
      },
    });
  }, [getUserProfile, loadedSuites, sessionId, fileMetrics]);

  // Track when user leaves the page after generating suggestions but before finalizing
  useEffect(() => {
    const handlePageLeave = () => {
      // Only fire if suggestions were generated (resultData exists) and not already finalized
      if (!resultData || telemetryFiredRef.current.has(`RO_Finalized-${sessionId || 'no-session'}`)) return;

      const removed = resultData?.removed || [];
      const recs = resultData?.recommendations || [];
      const totalOptimisableTCs = recs.reduce((sum, rec) => sum + (rec.source_tc_ids || []).length, 0);

      // Count decisions made so far (user may have partially reviewed)
      const removalAccepted = Object.values(reviewDecisions.removals).filter(v => v === 'accepted').length;
      const removalRejected = Object.values(reviewDecisions.removals).filter(v => v === 'rejected').length;
      const recAccepted = Object.values(reviewDecisions.recommendations).filter(v => v === 'accepted').length;
      const recRejected = Object.values(reviewDecisions.recommendations).filter(v => v === 'rejected').length;

      appInsights.trackEvent({
        name: 'RO_SessionAbandoned',
        properties: {
          source: 'RegressionOptimizer',
          userId: (getUserProfile?.() || {}).email || 'anonymous',
          userName: (getUserProfile?.() || {}).name || 'anonymous',
          sessionId: sessionId || '',
          suiteNames: loadedSuites.map(s => s.name || s.file?.name || 'unknown').join(', '),
          suiteCount: String(loadedSuites.length),
          totalTestCases: String(fileMetrics?.testCases || 0),
          duplicateRemovalSuggestions: String(removed.length),
          parameterisationSuggestions: String(recs.length),
          totalOptimisableTCs: String(totalOptimisableTCs),
          removalsAccepted: String(removalAccepted),
          removalsRejected: String(removalRejected),
          recommendationsAccepted: String(recAccepted),
          recommendationsRejected: String(recRejected),
          outcome: 'abandoned',
        },
      });
    };

    window.addEventListener('beforeunload', handlePageLeave);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') handlePageLeave();
    });
    return () => {
      window.removeEventListener('beforeunload', handlePageLeave);
    };
  }, [resultData, reviewDecisions, sessionId, loadedSuites, fileMetrics, getUserProfile]);



  // --- Cumulative metrics from all loaded suites ---
  const cumulativeMetrics = useMemo(() => {
    const loaded = loadedSuites.filter(s => s.metrics);
    if (!loaded.length) return null;
    return {
      testCases: loaded.reduce((sum, s) => sum + (s.metrics?.testCases || 0), 0),
      totalSteps: loaded.reduce((sum, s) => sum + (s.metrics?.totalSteps || 0), 0),
    };
  }, [loadedSuites]);

  // --- Parse a file for metrics and add it as a suite ---
  const parseSuiteMetrics = useCallback(async (targetFile) => {
    if (!targetFile) return null;
    try {
      const data = await regressionOptimizerApi.parse(targetFile, { sheet: sheetName || undefined });
      const idCol = data.id_column;
      const rows = data.rows || [];
      const totalSteps = data.total_steps ?? rows.length;
      if (idCol) {
        const tcIds = new Set(rows.map(r => String(r[idCol] || '').trim()).filter(Boolean));
        return { testCases: tcIds.size, totalSteps };
      }
      return { testCases: rows.length, totalSteps };
    } catch {
      return null;
    }
  }, [sheetName]);

  // --- Add suite with TC limit check ---
  const addSuite = useCallback(async (suiteObj) => {
    // Parse metrics first
    const metrics = await parseSuiteMetrics(suiteObj.file);
    const suiteWithMetrics = { ...suiteObj, metrics };

    // Check TC limit
    const currentTotal = loadedSuites.reduce((sum, s) => sum + (s.metrics?.testCases || 0), 0);
    const newCount = metrics?.testCases || 0;
    if (currentTotal + newCount > MAX_TC_LIMIT) {
      setLimitDialog({ pendingSuite: suiteWithMetrics, currentTotal, newCount });
      return;
    }

    setLoadedSuites(prev => [...prev, suiteWithMetrics]);
    // Also set as the active file (latest or single)
    setFile(suiteObj.file);
    setFileMetrics(prev => {
      const prevTc = prev?.testCases || 0;
      const prevSteps = prev?.totalSteps || 0;
      return {
        testCases: prevTc + (metrics?.testCases || 0),
        totalSteps: prevSteps + (metrics?.totalSteps || 0),
      };
    });
  }, [loadedSuites, parseSuiteMetrics]);

  // --- Confirm adding a suite despite TC limit (proceed without the over-limit one) ---
  const confirmLimitContinue = useCallback(() => {
    // User chose to continue with current suites, dismissing the new one
    setLimitDialog(null);
    notify(`Continuing with ${loadedSuites.length} suite${loadedSuites.length !== 1 ? 's' : ''} (${loadedSuites.reduce((s, x) => s + (x.metrics?.testCases || 0), 0)} TCs)`, 'info');
  }, [loadedSuites, notify]);

  // --- File handling ---

  const handleDrop = useCallback((e) => {

    e.preventDefault();

    setDragging(false);

    const dropped = e.dataTransfer.files[0];
    if (dropped) {
      addSuite({ id: Date.now().toString(), file: dropped, meta: null, metrics: null, source: 'file', name: dropped.name });
    }

  }, [addSuite]);



  const handleFileSelect = (e) => {

    const f = e.target.files[0];
    if (f) {
      addSuite({ id: Date.now().toString(), file: f, meta: null, metrics: null, source: 'file', name: f.name });
      if (fileInputRef.current) fileInputRef.current.value = '';
    }

  };

  const handleAdoImport = async () => {
    const isUrlMode = adoInputMode === 'url';
    if (isUrlMode && !adoUrl.trim()) return;
    if (!isUrlMode && (!adoProject.trim() || !adoPlanId.trim() || !adoSuiteId.trim())) return;

    setAdoImporting(true);
    setAdoError('');
    try {
      const param = isUrlMode
        ? adoUrl.trim()
        : { project: adoProject.trim(), planId: adoPlanId.trim(), suiteId: adoSuiteId.trim() };

      // Try tree import first (discovers child suites under a parent folder)
      let treeResult = null;
      try {
        treeResult = await regressionOptimizerApi.importFromAdoTree(param);
      } catch (treeErr) {
        console.warn('[RO] Tree import failed, falling back to single-suite:', treeErr.message);
      }

      if (treeResult && treeResult.suites.length > 0) {
        // Tree returned suites — import all at once to preserve DFS order
        if (treeResult.suites.length > 1) {
          notify(`Found ${treeResult.suites.length} child suites under "${treeResult.parentName}". Importing...`, 'info');
        }
        // Parse metrics for all suites in parallel, then add in one batch
        const parsed = await Promise.all(treeResult.suites.map(async (suite) => {
          const suiteName = suite.meta?.suiteName || `ADO Suite ${suite.meta?.suiteId || ''}`.trim();
          const metrics = await parseSuiteMetrics(suite.file);
          return {
            id: `${Date.now()}-${suite.meta.suiteId}`,
            file: suite.file,
            meta: suite.meta,
            metrics,
            source: treeResult.suites.length > 1 ? 'ado-tree' : 'ado',
            name: suiteName,
          };
        }));
        setLoadedSuites(prev => [...prev, ...parsed]);
        const totalMetrics = parsed.reduce((acc, s) => ({
          testCases: acc.testCases + (s.metrics?.testCases || 0),
          totalSteps: acc.totalSteps + (s.metrics?.totalSteps || 0),
        }), { testCases: 0, totalSteps: 0 });
        setFileMetrics(prev => ({
          testCases: (prev?.testCases || 0) + totalMetrics.testCases,
          totalSteps: (prev?.totalSteps || 0) + totalMetrics.totalSteps,
        }));
        if (parsed.length) setFile(parsed[parsed.length - 1].file);
        if (treeResult.errors?.length > 0) {
          notify(`${treeResult.errors.length} suite(s) had no test cases and were skipped.`, 'warning');
        }
        if (treeResult.warning) {
          setAdoError(treeResult.warning);
        }
      } else if (!treeResult) {
        // Tree endpoint not available — fall back to single-suite import
        const { file: importedFile, meta } = await regressionOptimizerApi.importFromAdo(param);
        const suiteName = meta?.suiteName || `ADO Suite ${meta?.suiteId || ''}`.trim();
        await addSuite({ id: Date.now().toString(), file: importedFile, meta, metrics: null, source: 'ado', name: suiteName });
      } else {
        // Tree returned 0 suites — parent has no test cases in any child
        throw new Error('No test cases found in this suite or any of its child suites.');
      }

      setAdoUrl('');
      setAdoProject('');
      setAdoPlanId('');
      setAdoSuiteId('');
    } catch (err) {
      setAdoError(err.message || 'Failed to import from Azure DevOps');
    } finally {
      setAdoImporting(false);
    }
  };



  const removeSuite = (suiteId) => {
    setLoadedSuites(prev => {
      const next = prev.filter(s => s.id !== suiteId);
      // Update fileMetrics from remaining suites
      const metrics = next.reduce((acc, s) => ({
        testCases: acc.testCases + (s.metrics?.testCases || 0),
        totalSteps: acc.totalSteps + (s.metrics?.totalSteps || 0),
      }), { testCases: 0, totalSteps: 0 });
      setFileMetrics(next.length ? metrics : null);
      if (!next.length) {
        setFile(null);
      } else {
        setFile(next[next.length - 1].file);
      }
      return next;
    });
  };

  const removeAllSuites = () => {

    setFile(null);

    setFileMetrics(null);
    setLoadedSuites([]);
    setLimitDialog(null);

    setUploadMode('ado');

    if (fileInputRef.current) fileInputRef.current.value = '';

    // Reset all state when file is removed
    setDomainClassification(null);
    setSelectedDomainAgent(null);
    setTrainingUploadDomain(null);
    setSessionUploadedFiles([]);
    setShowConfirmStart(false);
    skipTrainingRef.current = false;
  };



  // --- Optimize (full review flow) ---

  const runOptimize = async () => {

    if (!file && !loadedSuites.length) return;

    // If multiple suites, merge them first
    let activeFile = file;
    if (loadedSuites.length > 1) {
      try {
        notify('Merging test suites...', 'info');
        const suiteLabels = loadedSuites.map((s, idx) =>
          s.source === 'ado' && s.meta?.suiteId ? `#${s.meta.suiteId}` : `#${s.id.slice(-6)}`
        );
        activeFile = await regressionOptimizerApi.mergeFiles(
          loadedSuites.map(s => s.file),
          { sheet: sheetName || undefined, suiteLabels }
        );
        setFile(activeFile);
      } catch (err) {
        notify(err.message || 'Failed to merge test suites', 'error');
        return;
      }
    } else if (loadedSuites.length === 1) {
      activeFile = loadedSuites[0].file;
      setFile(activeFile);
    }

    if (!activeFile) return;



    // Create AbortController for this run

    const controller = new AbortController();

    abortRef.current = controller;



    setIsOptimizing(true);

    setReviewVisible(false);

    setResultData(null);

    setOriginalData(null);

    setProgressPct(0);

    setElapsedSec(0);

    setPipelineStatus('Classifying test suite...');

    setStageStates({});

    pipelineDoneRef.current = false;

    // If skip_training is set, we know the backend won't block -- show pipeline immediately
    if (skipTrainingRef.current) {
      setPipelineVisible(true);
      setPipelineStatus('Initializing optimization...');
      runStartRef.current = Date.now();
      elapsedRef.current = setInterval(() => {
        setElapsedSec(Math.floor((Date.now() - runStartRef.current) / 1000));
      }, 1000);
      const stageDurations = [3000, 8000, 18000, 6000, 22000, 3000];
      let cs = 0;
      const adv = () => {
        if (pipelineDoneRef.current) return;
        if (cs < PROGRESS_STAGES.length) {
          const s = PROGRESS_STAGES[cs];
          setPipelineStatus(s.text);
          setProgressPct(s.pct);
          if (s.stage) {
            setStageStates(prev => {
              const next = { ...prev };
              if (cs > 0 && PROGRESS_STAGES[cs - 1].stage) next[PROGRESS_STAGES[cs - 1].stage] = 'completed';
              next[s.stage] = 'active';
              return next;
            });
          }
          cs++;
          if (cs < PROGRESS_STAGES.length) timerRef.current = setTimeout(adv, stageDurations[cs - 1]);
        }
      };
      timerRef.current = setTimeout(adv, 500);
    }



    const options = {

      sheet: sheetName || undefined,

      embeddingThreshold: threshold,

      temperature,

      signal: controller.signal,

      skipTraining: skipTrainingRef.current,

    };



    try {

      // Parse original data (for diff view) -- also populates file metrics immediately

      const parsePromise = regressionOptimizerApi.parse(activeFile, options)

        .then(data => {

          if (!controller.signal.aborted) {

            setOriginalData(data);

            const idCol = data.id_column;

            const rows = data.rows || [];

            const totalSteps = data.total_steps ?? rows.length;

            if (idCol) {

              const tcIds = new Set(rows.map(r => String(r[idCol] || '').trim()).filter(Boolean));

              setFileMetrics({ testCases: tcIds.size, totalSteps });

            } else {

              setFileMetrics({ testCases: rows.length, totalSteps });

            }

          }

        })

        .catch(() => null);



      // Get optimization summary

      const summaryData = await regressionOptimizerApi.optimizeSummary(activeFile, options);



      // Guard: if aborted between fetch completing and here, bail

      if (controller.signal.aborted) return;

      // Handle blocked response (domain training prompt) -- no pipeline shown
      if (summaryData.blocked) {
        setIsOptimizing(false);
        setPipelineVisible(false);
        setDomainClassification(summaryData);
        setSelectedDomainAgent(summaryData.domain_agent);
        setTrainingUploadDomain(summaryData.domain_agent);
        notify(`Domain "${summaryData.domain_display_name}" detected. Upload reference materials for better results, or proceed.`, 'info');
        return;
      }

      // Not blocked -- show pipeline if not already started (skip path already has it running)
      if (!skipTrainingRef.current) {
        setPipelineVisible(true);
        runStartRef.current = Date.now();
        elapsedRef.current = setInterval(() => {
          setElapsedSec(Math.floor((Date.now() - runStartRef.current) / 1000));
        }, 1000);
        const stageDurations = [3000, 8000, 18000, 6000, 22000, 3000];
        let currentStage = 0;
        const advanceStage = () => {
          if (pipelineDoneRef.current) return;
          if (currentStage < PROGRESS_STAGES.length) {
            const s = PROGRESS_STAGES[currentStage];
            setPipelineStatus(s.text);
            setProgressPct(s.pct);
            if (s.stage) {
              setStageStates(prev => {
                const next = { ...prev };
                if (currentStage > 0 && PROGRESS_STAGES[currentStage - 1].stage) {
                  next[PROGRESS_STAGES[currentStage - 1].stage] = 'completed';
                }
                next[s.stage] = 'active';
                return next;
              });
            }
            currentStage++;
            if (currentStage < PROGRESS_STAGES.length) {
              timerRef.current = setTimeout(advanceStage, stageDurations[currentStage - 1]);
            }
          }
        };
        timerRef.current = setTimeout(advanceStage, 500);
      }

      await parsePromise;



      clearTimeout(timerRef.current);

      clearInterval(elapsedRef.current);

      pipelineDoneRef.current = true;

      // Rapidly complete remaining stages one by one (200ms each) for visual polish
      const completeStages = async () => {
        for (const s of PIPELINE_STAGES) {
          setStageStates(prev => {
            const next = { ...prev };
            next[s.key] = 'completed';
            return next;
          });
          await new Promise(r => setTimeout(r, 150));
        }
        setProgressPct(100);
        const agentName = summaryData.classification?.suggested_agents?.[0]?.display_name;
        setPipelineStatus(agentName
          ? `Complete! Optimized using ${agentName} agent.`
          : 'Complete! Review proposed changes below.');
      };
      await completeStages();

      if (summaryData.domain_agent) setSelectedDomainAgent(summaryData.domain_agent);



      setResultData(summaryData);

      setSessionId(summaryData.session_id);



      // Initialize review decisions

      const removals = {};

      const idCol = summaryData.id_column;

      (summaryData.removed || []).forEach(r => {

        const tcId = String(r[idCol] || '').trim();

        if (tcId) removals[tcId] = 'pending';

      });

      const recommendations = {};

      (summaryData.recommendations || []).forEach((_, idx) => {

        recommendations[idx] = 'pending';

      });

      setReviewDecisions({ removals, recommendations });

      setReviewVisible(true);

      setActiveTab('diff');

      // --- Telemetry: Optimization suggestions generated ---
      const removedArr = summaryData.removed || [];
      const recsArr = summaryData.recommendations || [];
      const totalOptimisable = recsArr.reduce((s, rec) => s + (rec.source_tc_ids || []).length, 0);
      trackOptimizerEvent('RO_SuggestionsGenerated', {
        duplicateRemovalSuggestions: String(removedArr.length),
        parameterisationSuggestions: String(recsArr.length),
        totalOptimisableTCs: String(totalOptimisable),
        domainAgent: summaryData.domain_agent || '',
        uniqueIntents: String(summaryData.summary?.unique_intents || 0),
        coverageComplete: String(summaryData.guardrail?.coverage_complete ?? ''),
        elapsedSeconds: String(Math.floor((Date.now() - (runStartRef.current || Date.now())) / 1000)),
      });



    } catch (err) {

      clearTimeout(timerRef.current);

      clearInterval(elapsedRef.current);

      // If aborted by user, show friendly message and reset

      if (err.name === 'AbortError' || controller.signal.aborted) {

        setPipelineVisible(false);

        setProgressPct(0);

        setPipelineStatus('Initializing...');

        setStageStates({});

        notify('Optimization stopped', 'info');

      } else {

        setPipelineStatus('Error: ' + err.message);

        setProgressPct(0);

        notify(err.message, 'error');

      }

    } finally {

      setIsOptimizing(false);

      abortRef.current = null;

    }

  };



  // --- Stop handler ---

  const stopOptimize = () => {

    if (abortRef.current) {

      abortRef.current.abort();

    }

  };



  // --- Estimated remaining time helper ---

  const getEstimatedRemaining = () => {

    const remaining = TOTAL_EST_SEC - elapsedSec;

    if (remaining > 0) return `~${remaining}s remaining`;

    return 'Taking longer than expected \u2014 still working\u2026';

  };



  const formatElapsed = (sec) => {

    const m = Math.floor(sec / 60);

    const s = sec % 60;

    return m > 0 ? `${m}m ${s.toString().padStart(2, '0')}s` : `${s}s`;

  };



  // --- Review actions ---

  const setRemovalDecision = (tcId, decision) => {

    setReviewDecisions(prev => ({

      ...prev,

      removals: { ...prev.removals, [tcId]: decision },

    }));

  };



  const setRecommendationDecision = (idx, decision) => {

    setReviewDecisions(prev => ({

      ...prev,

      recommendations: { ...prev.recommendations, [idx]: decision },

    }));

  };



  const acceptAll = () => {

    setReviewDecisions(prev => {

      const removals = {};

      Object.keys(prev.removals).forEach(k => { removals[k] = 'accepted'; });

      const recommendations = {};

      Object.keys(prev.recommendations).forEach(k => { recommendations[k] = 'accepted'; });

      return { removals, recommendations };

    });

  };



  const rejectAll = () => {

    setReviewDecisions(prev => {

      const removals = {};

      Object.keys(prev.removals).forEach(k => { removals[k] = 'rejected'; });

      const recommendations = {};

      Object.keys(prev.recommendations).forEach(k => { recommendations[k] = 'rejected'; });

      return { removals, recommendations };

    });

  };



  // --- Finalize ---

  const allReviewed = Object.values(reviewDecisions.removals).every(v => v !== 'pending') &&

    Object.values(reviewDecisions.recommendations).every(v => v !== 'pending');



  const [showFormatDialog, setShowFormatDialog] = useState(false);
  const [showOriginalFile, setShowOriginalFile] = useState(false);
  const [showTrainingFiles, setShowTrainingFiles] = useState(false);
  const [isUploadingTraining, setIsUploadingTraining] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);



  const handleConfirmClick = () => {

    if (!sessionId) { notify('No session found. Re-run optimization.', 'error'); return; }

    setShowFormatDialog(true);

  };



  const syncFeedbackFromEditor = useCallback(() => {

    const el = feedbackEditorRef.current;

    if (!el) return;

    const safeHtml = sanitizeFeedbackHtml(el.innerHTML);

    setUserFeedbackHtml(safeHtml);

    setUserFeedbackText((el.innerText || '').trim());

  }, []);



  const applyFeedbackFormat = (command) => {

    if (!feedbackEditorRef.current) return;

    feedbackEditorRef.current.focus();

    document.execCommand(command, false, null);

    syncFeedbackFromEditor();

  };



  const clearFeedbackEditor = () => {

    if (!feedbackEditorRef.current) return;

    feedbackEditorRef.current.innerHTML = '';

    setUserFeedbackHtml('');

    setUserFeedbackText('');

  };



  const runFinalize = async (chosenFormat) => {

    setShowFormatDialog(false);

    if (!sessionId) { notify('No session found. Re-run optimization.', 'error'); return; }



    const acceptedRemovals = [];

    const rejectedRemovals = [];

    Object.entries(reviewDecisions.removals).forEach(([id, d]) => {

      if (d === 'accepted') {

        acceptedRemovals.push(id);

      } else if (d === 'accepted-swap') {

        rejectedRemovals.push(id);

        const removedRow = removed.find(r => String(r[idColumn] || '').trim() === id);

        const reason = removedRow?.['Removal Reason'] || '';

        const similarId = extractSimilarTcId(reason);

        if (similarId) acceptedRemovals.push(similarId);

      } else {

        rejectedRemovals.push(id);

      }

    });



    const acceptedRecommendations = [];

    const rejectedRecommendations = [];

    Object.entries(reviewDecisions.recommendations).forEach(([id, d]) => {

      const n = parseInt(id, 10);

      (d === 'accepted' ? acceptedRecommendations : rejectedRecommendations).push(n);

    });



    setIsOptimizing(true);

    setIsDownloading(true);

    try {

      const blob = await regressionOptimizerApi.finalize({

        sessionId,

        acceptedRemovals,

        rejectedRemovals,

        acceptedRecommendations,

        rejectedRecommendations,

        outputFormat: chosenFormat,

        userFeedbackHtml,

        userFeedbackText: [userFeedbackText, ...Object.entries(rejectFeedback).filter(([, v]) => v?.trim()).map(([k, v]) => `[${k}] ${v}`)].filter(Boolean).join('\n'),

      });

      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');

      a.href = url;

      a.download = (file ? file.name.replace(/\.[^.]+$/, '') : 'output') + `_optimized_${chosenFormat === 'steps' ? 'traditional' : chosenFormat}.xlsx`;

      a.click();

      URL.revokeObjectURL(url);

      notify('Optimized file downloaded!', 'success');

      // --- Telemetry: Finalization complete ---
      const recsAcceptedTCs = acceptedRecommendations.reduce((sum, idx) => {
        const rec = (resultData?.recommendations || [])[idx];
        return sum + (rec?.source_tc_ids || []).length;
      }, 0);
      trackOptimizerEvent('RO_Finalized', {
        duplicatesAccepted: String(acceptedRemovals.length),
        duplicatesRejected: String(rejectedRemovals.length),
        parameterisationsAccepted: String(acceptedRecommendations.length),
        parameterisationsRejected: String(rejectedRecommendations.length),
        totalTestsOptimised: String(acceptedRemovals.length + recsAcceptedTCs),
        outputFormat: chosenFormat,
        outcome: 'downloaded',
      });

    } catch (err) {

      const msg = err.message || String(err);

      if (msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('fetch')) {

        notify('Server is not reachable. Please ensure the backend is running and try again.', 'error');

      } else if (msg.includes('Session not found') || msg.includes('expired')) {

        notify('Session expired -- please re-run the optimization.', 'error');

      } else {

        notify(msg, 'error');

      }

    } finally {

      setIsOptimizing(false);

      setIsDownloading(false);

    }

  };



  // --- Reset ---

  const resetAll = () => {

    setFile(null);

    setFileMetrics(null);
    setLoadedSuites([]);
    setLimitDialog(null);

    setUploadMode('ado');

    setResultData(null);

    setOriginalData(null);

    setSessionId(null);

    setPipelineVisible(false);

    setReviewVisible(false);

    setProgressPct(0);

    setElapsedSec(0);

    setPipelineStatus('Initializing...');

    setStageStates({});

    setReviewDecisions({ removals: {}, recommendations: {} });

    setUserFeedbackHtml('');

    setUserFeedbackText('');

    setRejectFeedback({});

    setFeedbackConfirmed({});

    setDomainClassification(null);

    setSelectedDomainAgent(null);

    setTrainingUploadDomain(null);

    setSkipTraining(false);

    skipTrainingRef.current = false;

    setSessionUploadedFiles([]);

    setShowConfirmStart(false);

    if (feedbackEditorRef.current) {

      feedbackEditorRef.current.innerHTML = '';

    }

    setActiveTab('diff');

    setExpandedCols(new Set());

    setActiveBadge(null);

    // Reset config fields

    setSheetName('');

    setThreshold(0.96);

    setTemperature(0.0);

    if (timerRef.current) clearTimeout(timerRef.current);

    if (elapsedRef.current) clearInterval(elapsedRef.current);

  };



  // --- Computed values ---

  const summary = resultData?.summary;

  const removed = resultData?.removed || [];

  const recommendations = resultData?.recommendations || [];

  const idColumn = resultData?.id_column;



  const removalCounts = {

    accepted: Object.values(reviewDecisions.removals).filter(v => v === 'accepted').length,

    rejected: Object.values(reviewDecisions.removals).filter(v => v === 'rejected').length,

    pending: Object.values(reviewDecisions.removals).filter(v => v === 'pending').length,

  };

  const recCounts = {

    accepted: Object.values(reviewDecisions.recommendations).filter(v => v === 'accepted').length,

    rejected: Object.values(reviewDecisions.recommendations).filter(v => v === 'rejected').length,

    pending: Object.values(reviewDecisions.recommendations).filter(v => v === 'pending').length,

  };



  // --- Build diff/optimized/removed tab data ---

  const removedMap = {};

  if (idColumn) {

    removed.forEach(r => {

      const tcId = String(r[idColumn] || '').trim();

      if (tcId) removedMap[tcId] = r['Removal Reason'] || 'Similar to another test case';

    });

  }



  // --- Step-column collapse helpers (mirrors standalone col-toggle) ---

  const STEP_COL_NAMES = ['test step', 'step action', 'step expected'];

  const normalizeFieldLabel = (label) => String(label || '').trim().toLowerCase().replace(/\s+/g, ' ');

  const isStepCol = (c) => STEP_COL_NAMES.includes(c.trim().toLowerCase());

  const getDefaultColumnWidth = (columnName) => {

    const normalized = normalizeFieldLabel(columnName);

    if (normalized === 'status' || normalized === 'state' || normalized === 'type') return 84;

    if (normalized.includes('priority')) return 92;

    if (normalized === normalizeFieldLabel(idColumn) || normalized.includes('id')) return 110;

    if (normalized.includes('title') || normalized.includes('feature')) return 290;

    if (normalized.includes('test step') || normalized.includes('step action') || normalized.includes('action') || normalized.includes('parameters')) return 190;

    if (normalized.includes('expected') || normalized.includes('assertion')) return 220;

    if (normalized.includes('reason')) return 260;

    return 140;

  };

  const getColumnWidth = useCallback((columnName) => {

    const key = normalizeFieldLabel(columnName);

    return columnWidths[key] || getDefaultColumnWidth(columnName);

  }, [columnWidths, idColumn]);

  const getColumnStyle = useCallback((columnName) => {

    const width = getColumnWidth(columnName);

    return {

      width: `${width}px`,

      minWidth: `${width}px`,

      maxWidth: `${width}px`,

    };

  }, [getColumnWidth]);

  const onColumnResizeMove = useCallback((event) => {

    const state = resizeStateRef.current;

    if (!state) return;

    const delta = event.clientX - state.startX;

    const nextWidth = Math.max(70, Math.min(900, state.startWidth + delta));

    setColumnWidths(prev => ({ ...prev, [state.key]: nextWidth }));

  }, []);

  const stopColumnResize = useCallback(() => {

    resizeStateRef.current = null;

    window.removeEventListener('mousemove', onColumnResizeMove);

    window.removeEventListener('mouseup', stopColumnResize);

    document.body.classList.remove('ro-resizing-columns');

  }, [onColumnResizeMove]);

  const startColumnResize = useCallback((event, columnName) => {

    event.preventDefault();

    event.stopPropagation();

    const key = normalizeFieldLabel(columnName);

    resizeStateRef.current = {

      key,

      startX: event.clientX,

      startWidth: getColumnWidth(columnName),

    };

    document.body.classList.add('ro-resizing-columns');

    window.addEventListener('mousemove', onColumnResizeMove);

    window.addEventListener('mouseup', stopColumnResize);

  }, [getColumnWidth, onColumnResizeMove, stopColumnResize]);

  const renderColumnResizer = (columnName) => (

    <span

      className="ro-col-resizer"

      role="separator"

      aria-orientation="vertical"

      aria-label={`Resize ${columnName} column`}

      tabIndex={0}

      onClick={(e) => e.stopPropagation()}

      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); }}

      onMouseDown={(e) => startColumnResize(e, columnName)}

    />

  );

  const formatCellValue = (value) => {

    if (value == null) return '';

    return String(value).replace(/^\s+/, '');

  };

  const getColumnClass = (columnName, idFieldName = null) => {

    const normalized = String(columnName || '').trim().toLowerCase();

    const normalizedId = String(idFieldName || '').trim().toLowerCase();



    if (normalizedId && normalized === normalizedId) return 'ro-col-id';

    if (normalized === 'status') return 'ro-col-status';

    if (normalized.includes('priority')) return 'ro-col-priority';

    if (normalized.includes('title')) return 'ro-col-title';

    if (normalized.includes('step action') || normalized === 'test step') return 'ro-col-step-action';

    if (normalized.includes('expected')) return 'ro-col-step-expected';

    if (normalized.includes('reason')) return 'ro-col-reason';

    return 'ro-col-generic';

  };



  // Split concatenated step text into individual steps for display

  const splitSteps = (text) => {

    if (!text || !text.trim()) return null;

    // First split on pipe separator (literal split avoids regex backtracking)

    const parts = text.split('|').map(s => s.trim()).filter(Boolean);

    // Then within each part, split on numbered markers like "1. ", "2) ", "Step 3: "

    const steps = [];

    parts.forEach(part => {

      const subSteps = part.split(/(?=(?:^|\s)(?:step\s*)?\d+[.)\-:]\s)/i).filter(s => s.trim());

      subSteps.forEach(s => steps.push(s.trim()));

    });

    return steps.length > 0 ? steps : null;

  };



  // Render a comparison cell value; for step columns, split into an ordered list

  const renderComparisonValue = (label, value) => {

    if (!(value) || !value.trim()) return '--';

    if (isStepCol(label)) {

      const steps = splitSteps(value);

      if (steps && steps.length > 1) {

        return (

          <ol className="ro-step-list">

            {steps.map((s, i) => <li key={i}>{s.replace(/^(?:step\s*)?\d+[.)\-:]\s*/i, '')}</li>)}

          </ol>

        );

      }

    }

    return value;

  };

  const toggleCol = (colName) => {

    setExpandedCols(prev => {

      const next = new Set(prev);

      if (next.has(colName)) next.delete(colName);

      else next.add(colName);

      return next;

    });

  };



  useEffect(() => {

    if (!originalData?.columns?.length) return;

    const stepCols = originalData.columns.filter(isStepCol);

    if (!stepCols.length) return;



    setExpandedCols(prev => {

      const next = new Set(prev);

      stepCols.forEach(col => next.add(col));

      return next;

    });

  }, [originalData]);



  // --- Review comparison helpers ---

  const toggleReviewExpand = (key) => {

    setExpandedReviewItems(prev => {

      const next = new Set(prev);

      if (next.has(key)) next.delete(key);

      else next.add(key);

      return next;

    });

  };



  // Extract similar TC ID from removal reason like: '[Similarity] Semantically similar to "319887" (...)'

  const extractSimilarTcId = (reason) => {

    const match = reason?.match(/similar to "([^"]+)"/i);

    return match ? match[1] : null;

  };



  // Find a TC row from originalData by its ID

  const findTcById = (tcId) => {

    if (!originalData || !idColumn || !tcId) return null;

    const rows = originalData.rows || originalData;

    if (!Array.isArray(rows)) return null;

    return rows.find(row => String(row[idColumn] || '').trim() === String(tcId).trim());

  };



  // Get display fields (skip internal columns; keep empty values so comparison shows all fields)

  const getDisplayFields = (record) => {

    if (!record) return [];

    const skip = ['Removal Reason', '__index', '__row_index'];

    return Object.entries(record)

      .filter(([k]) => !skip.includes(k))

      .map(([k, v]) => ({ label: k, value: v != null ? String(v) : '' }));

  };



  useEffect(() => {

    return () => {

      window.removeEventListener('mousemove', onColumnResizeMove);

      window.removeEventListener('mouseup', stopColumnResize);

      document.body.classList.remove('ro-resizing-columns');

    };

  }, [onColumnResizeMove, stopColumnResize]);



  // --- Pipeline badge definitions (same order as standalone) ---

  const BADGE_DEFS = [

    { key: 'cosine_similarity',  label: 'Semantic Similarity' },

    { key: 'intent_extraction',  label: 'Intent Extraction' },

    { key: 'step_diffing',       label: 'Step Diffing' },

    { key: 'synthesis',          label: 'Synthesis' },

  ];



  // --- Top-level tab state ---
  const [topTab, setTopTab] = useState(0);

  // --- Render ---

  return (

    <div className="regression-optimizer-page">

      {/* --- Top-level tabs --- */}
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          mb: 2.5, borderRadius: 2, overflow: 'hidden',
          borderColor: '#e2e8f0',
        }}
      >
        <Tabs
          value={topTab}
          onChange={(_, v) => setTopTab(v)}
          sx={{
            '& .MuiTab-root': {
              fontWeight: 600, textTransform: 'none', py: 1.2,
              minHeight: 46, fontSize: 14,
            },
            '& .Mui-selected': { color: '#4f46e5' },
            '& .MuiTabs-indicator': { bgcolor: '#4f46e5', height: 3 },
          }}
        >
          <Tab label="Optimize Now" />
          <Tab label="Schedule Optimization" />
        </Tabs>
      </Paper>

      {topTab === 1 && (
        <ScheduleOptimization />
      )}

      {topTab === 0 && (
      <>

      {/* --- Header -- */}

      <div className="ro-header">

        <div className="logo">

          <div className="logo-icon">

            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">

              <path d="M16 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8z"/><path d="M16 3v5h5"/>

            </svg>

          </div>

          <div>

            <div className="logo-title">Regression Test Suite Optimizer</div>

            <div className="logo-subtitle">AI-Powered Test Optimization</div>

          </div>

        </div>

        <div className="header-right">

          <div className="ro-api-status">

            <span className={`ro-status-dot ${healthStatus?.error ? 'err' : healthStatus ? 'ok' : ''}`}></span>

            <span style={{ color: 'var(--ro-text-secondary)' }}>

              {healthStatus?.error

                ? 'API Unreachable'

                : healthStatus?.llm_provider

                  ? `${healthStatus.llm_provider} / ${healthStatus.llm_model}`

                  : healthStatus

                    ? 'API Ready (No LLM)'

                    : 'Checking...'}

            </span>

          </div>

        </div>

      </div>



      {/* --- Hero (only show when no results) -- */}

      {!resultData && !pipelineVisible && (

        <section className="ro-hero">

          <h1 className="ro-hero-title">

            <span className="ro-gradient-text">Optimize</span> Your Regression Test Suite

          </h1>

          <p className="ro-hero-desc">

            Upload your Excel test cases and let our AI-powered 5-stage pipeline identify redundancies,

            propose merges, and ensure complete coverage -- reducing maintenance without sacrificing quality.

          </p>

          <div className="ro-hero-features">

            <span className="ro-feature-pill">{'✓'} Redundancy Detection</span>

            <span className="ro-feature-pill">{'✓'} Intent Extraction</span>

            <span className="ro-feature-pill">{'✓'} Smart Parameterization</span>

          </div>

        </section>

      )}



      {/* --- Main content -- */}

      <div className="ro-main">



        {/* --- Upload & Config Section (two-column) -- */}

        {!resultData && (

          <>

          <div className="ro-upload-section" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 24, alignItems: 'stretch' }}>

            {/* Left: Upload card */}

            <div className="ro-glass-card" style={{ display: 'flex', flexDirection: 'column' }}>

              <div style={{ marginBottom: 16 }}>

                <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: 'var(--ro-text-primary)' }}>

                  {loadedSuites.length > 1 ? 'Load Test Suites' : 'Load Test Suite'}

                </h3>

                <p style={{ margin: 0, fontSize: 12, color: 'var(--ro-text-muted)' }}>

                  {loadedSuites.length > 0 ? 'Add more suites or start optimization' : 'Choose one of the methods below to get started'}

                </p>

              </div>

              {/* Source tabs — always visible so users can add more suites */}
              {!isOptimizing && (
                <>
                  <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', marginBottom: 16 }}>
                    {[
                      { key: 'ado', label: 'Azure DevOps', icon: (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                        </svg>
                      )},
                      { key: 'file', label: 'Upload File', icon: (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="17,8 12,3 7,8"/>
                          <line x1="12" y1="3" x2="12" y2="15"/>
                        </svg>
                      )},
                    ].map(tab => (
                      <button
                        key={tab.key}
                        onClick={() => setUploadMode(tab.key)}
                        style={{
                          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                          padding: '10px 0', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                          border: 'none', background: 'transparent',
                          color: uploadMode === tab.key ? '#4f46e5' : '#64748b',
                          borderBottom: uploadMode === tab.key ? '2px solid #4f46e5' : '2px solid transparent',
                          marginBottom: '-2px', transition: 'all 0.2s',
                        }}
                      >
                        {tab.icon} {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* File upload mode */}
                  {uploadMode === 'file' && (
                    <div
                      className={`ro-upload-zone ${dragging ? 'dragging' : ''}`}
                      role="button"
                      tabIndex={0}
                      onDrop={handleDrop}
                      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                      onDragLeave={() => setDragging(false)}
                      onClick={() => fileInputRef.current?.click()}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                      />
                      <div className="upload-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="17,8 12,3 7,8"/>
                          <line x1="12" y1="3" x2="12" y2="15"/>
                        </svg>
                      </div>
                      <div className="upload-text">
                        <strong>Drop your Excel file here</strong><br/>
                        <span style={{ fontSize: 12, color: 'var(--ro-text-muted)' }}>
                          or click to browse &bull; .xlsx / .xls
                        </span>
                      </div>
                    </div>
                  )}

                  {/* ADO link mode */}
                  {uploadMode === 'ado' && (
                    <div style={{ padding: '16px 0 4px' }}>
                      {/* Sub-tabs: Paste URL vs Enter Details */}
                      <div style={{ display: 'flex', gap: 4, marginBottom: 14, background: '#f1f5f9', borderRadius: 8, padding: 3 }}>
                        {[
                          { key: 'url', label: 'Paste URL' },
                          { key: 'fields', label: 'Enter Details' },
                        ].map(tab => (
                          <button
                            key={tab.key}
                            onClick={() => { setAdoInputMode(tab.key); setAdoError(''); }}
                            style={{
                              flex: 1, padding: '8px 12px', fontSize: 12, fontWeight: 600,
                              borderRadius: 6, border: 'none', cursor: 'pointer',
                              background: adoInputMode === tab.key ? '#fff' : 'transparent',
                              color: adoInputMode === tab.key ? '#4f46e5' : '#64748b',
                              boxShadow: adoInputMode === tab.key ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                              transition: 'all 0.2s',
                            }}
                          >
                            {tab.label}
                          </button>
                        ))}
                      </div>

                      {/* URL input mode */}
                      {adoInputMode === 'url' && (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2">
                              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                            </svg>
                            <span style={{ fontSize: 12, color: '#475569', lineHeight: 1.4 }}>
                              Paste the full URL from your Azure DevOps Test Plans page
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input
                              type="text"
                              placeholder="https://dev.azure.com/org/project/_testPlans/define?planId=...&suiteId=..."
                              value={adoUrl}
                              onChange={(e) => { setAdoUrl(e.target.value); setAdoError(''); }}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleAdoImport(); }}
                              disabled={adoImporting}
                              style={{
                                flex: 1, padding: '10px 14px', fontSize: 13, borderRadius: 8,
                                border: adoError ? '1.5px solid #ef4444' : '1.5px solid #cbd5e1',
                                outline: 'none', background: '#fff', color: '#1e293b',
                                transition: 'border-color 0.2s',
                              }}
                              onFocus={(e) => { if (!adoError) e.target.style.borderColor = '#4f46e5'; }}
                              onBlur={(e) => { if (!adoError) e.target.style.borderColor = '#cbd5e1'; }}
                            />
                            <button
                              onClick={handleAdoImport}
                              disabled={!adoUrl.trim() || adoImporting}
                              style={{
                                padding: '10px 20px', fontSize: 13, fontWeight: 600, borderRadius: 8,
                                border: 'none',
                                background: !adoUrl.trim() || adoImporting ? '#e2e8f0' : 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                                color: !adoUrl.trim() || adoImporting ? '#94a3b8' : '#fff',
                                cursor: !adoUrl.trim() || adoImporting ? 'not-allowed' : 'pointer',
                                whiteSpace: 'nowrap', transition: 'all 0.2s',
                                boxShadow: !adoUrl.trim() || adoImporting ? 'none' : '0 2px 8px rgba(79,70,229,0.25)',
                              }}
                            >
                              {adoImporting ? (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span className="ro-btn-spinner" style={{ width: 12, height: 12 }}></span>
                                  Importing...
                                </span>
                              ) : 'Import'}
                            </button>
                          </div>
                        </>
                      )}

                      {/* Manual fields mode */}
                      {adoInputMode === 'fields' && (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2">
                              <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18"/><path d="M3 9h6"/>
                            </svg>
                            <span style={{ fontSize: 12, color: '#475569', lineHeight: 1.4 }}>
                              Enter your project name, plan ID, and suite ID from Azure DevOps
                            </span>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <div>
                              <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4, display: 'block' }}>Project Name</label>
                              <input
                                type="text"
                                placeholder="e.g. MyProject"
                                value={adoProject}
                                onChange={(e) => { setAdoProject(e.target.value); setAdoError(''); }}
                                disabled={adoImporting}
                                style={{
                                  width: '100%', padding: '9px 12px', fontSize: 13, borderRadius: 8,
                                  border: adoError ? '1.5px solid #ef4444' : '1.5px solid #cbd5e1',
                                  outline: 'none', background: '#fff', color: '#1e293b',
                                  transition: 'border-color 0.2s', boxSizing: 'border-box',
                                }}
                                onFocus={(e) => { if (!adoError) e.target.style.borderColor = '#4f46e5'; }}
                                onBlur={(e) => { if (!adoError) e.target.style.borderColor = '#cbd5e1'; }}
                              />
                            </div>
                            <div style={{ display: 'flex', gap: 10 }}>
                              <div style={{ flex: 1 }}>
                                <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4, display: 'block' }}>Plan ID</label>
                                <input
                                  type="text"
                                  placeholder="e.g. 12345"
                                  value={adoPlanId}
                                  onChange={(e) => { setAdoPlanId(e.target.value); setAdoError(''); }}
                                  disabled={adoImporting}
                                  style={{
                                    width: '100%', padding: '9px 12px', fontSize: 13, borderRadius: 8,
                                    border: adoError ? '1.5px solid #ef4444' : '1.5px solid #cbd5e1',
                                    outline: 'none', background: '#fff', color: '#1e293b',
                                    transition: 'border-color 0.2s', boxSizing: 'border-box',
                                  }}
                                  onFocus={(e) => { if (!adoError) e.target.style.borderColor = '#4f46e5'; }}
                                  onBlur={(e) => { if (!adoError) e.target.style.borderColor = '#cbd5e1'; }}
                                />
                              </div>
                              <div style={{ flex: 1 }}>
                                <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4, display: 'block' }}>Suite ID</label>
                                <input
                                  type="text"
                                  placeholder="e.g. 67890"
                                  value={adoSuiteId}
                                  onChange={(e) => { setAdoSuiteId(e.target.value); setAdoError(''); }}
                                  onKeyDown={(e) => { if (e.key === 'Enter') handleAdoImport(); }}
                                  disabled={adoImporting}
                                  style={{
                                    width: '100%', padding: '9px 12px', fontSize: 13, borderRadius: 8,
                                    border: adoError ? '1.5px solid #ef4444' : '1.5px solid #cbd5e1',
                                    outline: 'none', background: '#fff', color: '#1e293b',
                                    transition: 'border-color 0.2s', boxSizing: 'border-box',
                                  }}
                                  onFocus={(e) => { if (!adoError) e.target.style.borderColor = '#4f46e5'; }}
                                  onBlur={(e) => { if (!adoError) e.target.style.borderColor = '#cbd5e1'; }}
                                />
                              </div>
                            </div>
                            <button
                              onClick={handleAdoImport}
                              disabled={!adoProject.trim() || !adoPlanId.trim() || !adoSuiteId.trim() || adoImporting}
                              style={{
                                marginTop: 4, padding: '10px 20px', fontSize: 13, fontWeight: 600, borderRadius: 8,
                                border: 'none', width: '100%',
                                background: (!adoProject.trim() || !adoPlanId.trim() || !adoSuiteId.trim() || adoImporting) ? '#e2e8f0' : 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                                color: (!adoProject.trim() || !adoPlanId.trim() || !adoSuiteId.trim() || adoImporting) ? '#94a3b8' : '#fff',
                                cursor: (!adoProject.trim() || !adoPlanId.trim() || !adoSuiteId.trim() || adoImporting) ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s',
                                boxShadow: (!adoProject.trim() || !adoPlanId.trim() || !adoSuiteId.trim() || adoImporting) ? 'none' : '0 2px 8px rgba(79,70,229,0.25)',
                              }}
                            >
                              {adoImporting ? (
                                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                  <span className="ro-btn-spinner" style={{ width: 12, height: 12 }}></span>
                                  Importing...
                                </span>
                              ) : 'Import Test Suite'}
                            </button>
                          </div>
                        </>
                      )}

                      {adoError && (
                        <div style={{
                          fontSize: 12,
                          color: adoError.includes('limit') || adoError.includes('exceed') ? '#b45309' : '#dc2626',
                          background: adoError.includes('limit') || adoError.includes('exceed') ? '#fef3c7' : '#fef2f2',
                          border: `1px solid ${adoError.includes('limit') || adoError.includes('exceed') ? '#fcd34d' : '#fecaca'}`,
                          borderRadius: 8, padding: '8px 12px', marginTop: 8,
                          display: 'flex', alignItems: 'flex-start', gap: 6, lineHeight: 1.4,
                        }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                            stroke={adoError.includes('limit') || adoError.includes('exceed') ? '#b45309' : '#dc2626'}
                            strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}>
                            {adoError.includes('limit') || adoError.includes('exceed')
                              ? <><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>
                              : <><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></>
                            }
                          </svg>
                          <span>{adoError}</span>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Loaded suites list */}
              {loadedSuites.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {loadedSuites.length} Suite{loadedSuites.length !== 1 ? 's' : ''} Loaded
                    </span>
                    {loadedSuites.length > 1 && (
                      <button
                        onClick={removeAllSuites}
                        style={{
                          fontSize: 10, fontWeight: 600, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer',
                          padding: '2px 6px', borderRadius: 4, transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => { e.target.style.color = '#ef4444'; }}
                        onMouseLeave={(e) => { e.target.style.color = '#94a3b8'; }}
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 160, overflowY: 'auto' }}>
                    {loadedSuites.map((suite, idx) => (
                      <div
                        key={suite.id}
                        title={suite.name}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px',
                          borderRadius: 20,
                          background: (suite.source === 'ado' || suite.source === 'ado-tree') ? 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)' : '#f1f5f9',
                          border: (suite.source === 'ado' || suite.source === 'ado-tree') ? '1px solid #c7d2fe' : '1px solid #e2e8f0',
                          transition: 'all 0.2s', cursor: 'default', fontSize: 12,
                        }}
                      >
                        {(suite.source === 'ado' || suite.source === 'ado-tree') ? (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5" style={{ flexShrink: 0 }}>
                            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                          </svg>
                        ) : (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" style={{ flexShrink: 0 }}>
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                          </svg>
                        )}
                        <span style={{ fontWeight: 600, color: (suite.source === 'ado' || suite.source === 'ado-tree') ? '#4338ca' : '#334155' }}>
                          {(suite.source === 'ado' || suite.source === 'ado-tree') ? (suite.meta?.suiteId || suite.id) : suite.id.slice(-6)}
                        </span>
                        {!suite.metrics && (
                          <span className="ro-btn-spinner" style={{ width: 10, height: 10, borderColor: 'rgba(99,102,241,0.3)', borderTopColor: '#6366f1', flexShrink: 0 }}></span>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); removeSuite(suite.id); }}
                          style={{
                            color: '#b0b8c4', cursor: 'pointer', fontSize: 11, lineHeight: 1,
                            marginLeft: 2, transition: 'color 0.15s',
                            background: 'none', border: 'none', padding: 0,
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = '#b0b8c4'; }}
                          title={`Remove: ${suite.name}`}
                        >
                          {'\u2715'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* TC Limit exceeded dialog */}
              {limitDialog && (
                <div style={{
                  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  zIndex: 9999, backdropFilter: 'blur(2px)',
                }}>
                  <div style={{
                    background: '#fff', borderRadius: 16, padding: '28px 32px', maxWidth: 460, width: '92%',
                    boxShadow: '0 24px 64px rgba(0,0,0,0.25)', animation: 'ro-result-fade-in 0.3s ease',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                      <div style={{
                        width: 44, height: 44, borderRadius: 12,
                        background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                          <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                        </svg>
                      </div>
                      <div>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1e293b' }}>Test Case Limit Exceeded</h3>
                        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Maximum {MAX_TC_LIMIT} TCs per optimization</p>
                      </div>
                    </div>
                    <p style={{ margin: '0 0 8px', fontSize: 14, color: '#475569', lineHeight: 1.6 }}>
                      Adding <strong>{limitDialog.pendingSuite.name}</strong> ({limitDialog.newCount} TCs) would bring the
                      total to <strong style={{ color: '#dc2626' }}>{limitDialog.currentTotal + limitDialog.newCount}</strong> test cases,
                      exceeding the {MAX_TC_LIMIT} limit.
                    </p>
                    <p style={{ margin: '0 0 20px', fontSize: 13, color: '#64748b' }}>
                      You can continue with your current {loadedSuites.length} suite{loadedSuites.length !== 1 ? 's' : ''} ({limitDialog.currentTotal} TCs).
                    </p>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button
                        onClick={confirmLimitContinue}
                        style={{
                          flex: 1, padding: '11px 16px', fontSize: 13, fontWeight: 600, borderRadius: 10,
                          border: 'none', background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                          color: '#fff', cursor: 'pointer', boxShadow: '0 2px 8px rgba(79,70,229,0.25)',
                        }}
                      >
                        Continue with {limitDialog.currentTotal} TCs
                      </button>
                      <button
                        onClick={() => setLimitDialog(null)}
                        style={{
                          padding: '11px 20px', fontSize: 13, fontWeight: 600, borderRadius: 10,
                          border: '1px solid #e2e8f0', background: '#f8fafc', color: '#64748b', cursor: 'pointer',
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}





              {/* Similarity Threshold accordion */}
              {loadedSuites.length > 0 && !isOptimizing && (
                <div style={{ margin: '16px 0', borderRadius: 10, border: '1px solid #e0e7ff', overflow: 'hidden', background: '#fafbff' }}>
                  <button
                    onClick={() => setThresholdOpen(p => !p)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '12px 14px', border: 'none', background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)', cursor: 'pointer', textAlign: 'left' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>Fine-Tune Sensitivity</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#4f46e5', background: '#eef2ff', padding: '2px 8px', borderRadius: 5, border: '1px solid #c7d2fe' }}>
                        {Math.round(threshold * 100)}%
                      </span>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" style={{ transition: 'transform 0.2s', transform: thresholdOpen ? 'rotate(180deg)' : 'rotate(0)' }}><polyline points="6 9 12 15 18 9"/></svg>
                    </div>
                  </button>
                  {thresholdOpen && (
                    <div style={{ padding: '14px 16px 16px', borderTop: '1px solid #e0e7ff' }}>
                      <div style={{ position: 'relative', margin: '4px 0 8px' }}>
                        <div style={{
                          position: 'absolute', top: '50%', left: 0, right: 0, height: 6, borderRadius: 3,
                          background: 'linear-gradient(90deg, #fbbf24 0%, #6366f1 50%, #10b981 100%)',
                          transform: 'translateY(-50%)', opacity: 0.2, pointerEvents: 'none'
                        }} />
                        <input
                          type="range"
                          min="0.80"
                          max="1.00"
                          step="0.01"
                          value={threshold}
                          onChange={(e) => setThreshold(parseFloat(e.target.value))}
                          style={{ width: '100%', accentColor: '#4f46e5', cursor: 'pointer', position: 'relative', zIndex: 1 }}
                        />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                        <span style={{ fontSize: 10, fontWeight: 500, color: '#b45309' }}>Broader (80%)</span>
                        <span style={{ fontSize: 10, fontWeight: 500, color: '#059669' }}>Stricter (100%)</span>
                      </div>
                      <div style={{ textAlign: 'center', marginTop: 8, padding: '6px 10px', borderRadius: 6, background: 'rgba(99,102,241,0.04)' }}>
                        <span style={{ fontSize: 11, color: '#475569', lineHeight: 1.4 }}>
                          {threshold >= 0.97
                            ? 'Very conservative \u2014 only flags nearly identical test cases'
                            : threshold >= 0.95
                              ? 'Recommended \u2014 finds clear duplicates without over-flagging'
                              : threshold >= 0.93
                                ? 'Good balance \u2014 catches similar test cases while keeping unique ones'
                                : threshold >= 0.87
                                  ? 'Wider net \u2014 finds test cases covering similar areas'
                                  : 'Broad search \u2014 flags any test cases with overlapping coverage'}
                        </span>
                        {Math.round(threshold * 100) === 96 && (
                          <div style={{ marginTop: 4, fontSize: 10, fontWeight: 600, color: '#4f46e5' }}>\u2605 Recommended for most test suites</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Optimize / Stop buttons */}

              {!isOptimizing ? (

                <button

                  className="ro-optimize-btn"

                  disabled={!loadedSuites.length || showConfirmStart}

                  onClick={runOptimize}

                  style={{ display: showConfirmStart ? 'none' : undefined }}

                >

                  <span>Start Optimization</span>

                </button>

              ) : (

                <div className="ro-btn-group">

                  <button className="ro-optimize-btn ro-optimizing" disabled>

                    <span className="ro-btn-spinner"></span>

                    <span>Optimizing...</span>

                  </button>

                  <button className="ro-stop-btn" onClick={stopOptimize} title="Stop optimization">

                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="4" y="4" width="16" height="16" rx="2"/></svg>

                    <span>Stop</span>

                  </button>

                </div>

              )}



              {(resultData || pipelineVisible) && !isOptimizing && (

                <button className="ro-reset-btn" onClick={resetAll}>

                  {'\u21ba'} Reset

                </button>

              )}

            </div>



            {/* Right: Test Suite Overview metrics */}

            <div className="ro-glass-card" style={{ display: 'flex', flexDirection: 'column' }}>

              {/* Multi-suite layout */}
              {loadedSuites.length > 1 ? (<>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #6366f1, #4f46e5)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M9 17H7A5 5 0 0 1 7 7h2"/><path d="M15 7h2a5 5 0 1 1 0 10h-2"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--ro-text-primary)' }}>Combined Test Suites Overview</h3>
                    <span style={{ fontSize: 11, color: 'var(--ro-text-muted)' }}>{loadedSuites.length} suites loaded for cross-suite analysis</span>
                  </div>
                </div>

                {/* Aggregate metrics row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                  <div style={{ padding: '18px 16px', background: 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(99,102,241,0.03))', borderRadius: 12, border: '1px solid rgba(99,102,241,0.12)', textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" style={{ opacity: 0.7 }}><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
                      <div style={{ fontSize: 30, fontWeight: 800, color: 'var(--ro-primary)', lineHeight: 1 }}>{fileMetrics?.testCases ?? (summary?.original_count || '0')}</div>
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ro-text-muted)', marginTop: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Test Cases</div>
                  </div>
                  <div style={{ padding: '18px 16px', background: 'linear-gradient(135deg, rgba(16,185,129,0.08), rgba(16,185,129,0.03))', borderRadius: 12, border: '1px solid rgba(16,185,129,0.12)', textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" style={{ opacity: 0.7 }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                      <div style={{ fontSize: 30, fontWeight: 800, color: '#10b981', lineHeight: 1 }}>{fileMetrics?.totalSteps ?? (summary?.original_step_count || '0')}</div>
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ro-text-muted)', marginTop: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Steps</div>
                  </div>
                </div>

                {/* Per-suite breakdown */}
                <div style={{ borderRadius: 10, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                  <div style={{ padding: '10px 14px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Per-Suite Breakdown</span>
                  </div>
                  {(() => {
                    const hasTreeImport = loadedSuites.some(s => s.source === 'ado-tree');
                    const colors = ['#6366f1', '#0097A9', '#f59e0b', '#ef4444', '#8b5cf6', '#10b981'];

                    // --- Flat per-suite list (no folder hierarchy) ---
                    if (!hasTreeImport) {
                      return loadedSuites.map((s, idx) => {
                        const label = s.name || s.meta?.suiteName || 'Suite';
                        const tc = s.metrics?.testCases || 0;
                        const st = s.metrics?.totalSteps || 0;
                        const c = colors[idx % colors.length];
                        const suiteId = s.meta?.suiteId;
                        return (
                          <div key={s.id} style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '7px 12px',
                            borderBottom: '1px solid #f1f5f9',
                            background: '#fff',
                          }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                            </svg>
                            <span style={{
                              flex: 1, fontSize: 12, fontWeight: 600, color: '#334155',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
                            }} title={label}>
                              {label}
                            </span>
                            {suiteId && (
                              <span style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', background: '#f1f5f9', padding: '1px 6px', borderRadius: 4, whiteSpace: 'nowrap', flexShrink: 0, fontFamily: 'monospace' }}>#{suiteId}</span>
                            )}
                            <span style={{ fontSize: 11, fontWeight: 700, color: c, background: `${c}12`, padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap', flexShrink: 0 }}>{tc} TCs</span>
                            <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap', flexShrink: 0 }}>{st} steps</span>
                          </div>
                        );
                      });
                    }

                    // --- ADO folder tree (only when tree import was used) ---
                    // Build a tree from suite paths
                    const tree = { name: '__root__', children: {}, suites: [] };
                    let colorIdx = 0;

                    loadedSuites.forEach((s, loadIdx) => {
                      const pathStr = s.meta?.path || s.name || s.meta?.suiteName || 'Suite';
                      const parts = pathStr.split(' / ');
                      const pathIds = s.meta?.pathIds || [];
                      let node = tree;
                      // Walk/create intermediate folder nodes
                      for (let i = 0; i < parts.length - 1; i++) {
                        const folder = parts[i];
                        const folderSuiteId = parseInt(pathIds[i]) || 0;
                        if (!node.children[folder]) {
                          node.children[folder] = { name: folder, children: {}, suites: [], path: parts.slice(0, i + 1).join(' / '), folderSuiteId };
                        } else if (folderSuiteId && !node.children[folder].folderSuiteId) {
                          node.children[folder].folderSuiteId = folderSuiteId;
                        }
                        node = node.children[folder];
                      }
                      // Attach the suite at the leaf
                      node.suites.push({ ...s, _color: colors[colorIdx++ % colors.length] });
                    });

                    // Count total TCs in a subtree
                    const countTCs = (node) => {
                      let total = node.suites.reduce((n, s) => n + (s.metrics?.testCases || 0), 0);
                      Object.values(node.children).forEach(child => { total += countTCs(child); });
                      return total;
                    };

                    const toggleFolder = (path) => {
                      setExpandedFolders(prev => {
                        const next = new Set(prev);
                        if (next.has(path)) next.delete(path); else next.add(path);
                        return next;
                      });
                    };

                    // Render a tree node recursively
                    const renderNode = (node, depth = 0) => {
                      const childKeys = Object.keys(node.children).sort((a, b) => {
                        const aId = node.children[a].folderSuiteId || 0;
                        const bId = node.children[b].folderSuiteId || 0;
                        if (aId && bId) return bId - aId; // descending suite ID = ADO order
                        return a.localeCompare(b); // fallback alphabetical
                      });
                      const hasContent = childKeys.length > 0 || node.suites.length > 0;
                      if (!hasContent) return null;
                      // Sort leaf suites by descending suite ID to match ADO order
                      const sortedSuites = [...node.suites].sort((a, b) => {
                        const aId = parseInt(a.meta?.suiteId || '0');
                        const bId = parseInt(b.meta?.suiteId || '0');
                        if (aId && bId) return bId - aId; // descending suite ID = ADO order
                        return 0; // preserve original order for non-ADO suites
                      });
                      return (
                        <>
                          {/* Render leaf suites at this level first */}
                          {sortedSuites.map((s) => {
                            const label = s.name || s.meta?.suiteName || 'Suite';
                            const tc = s.metrics?.testCases || 0;
                            const st = s.metrics?.totalSteps || 0;
                            const c = s._color;
                            const suiteId = s.meta?.suiteId;
                            return (
                              <div key={s.id} style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                padding: `7px 12px 7px ${12 + depth * 20 + 18}px`,
                                borderBottom: '1px solid #f1f5f9',
                                background: '#fff',
                              }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                                </svg>
                                <span style={{
                                  flex: 1, fontSize: 12, fontWeight: 600, color: '#334155',
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
                                }} title={label}>
                                  {label.length > 30 ? label.slice(0, 28) + '…' : label}
                                </span>
                                {suiteId && (
                                  <span style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', background: '#f1f5f9', padding: '1px 6px', borderRadius: 4, whiteSpace: 'nowrap', flexShrink: 0, fontFamily: 'monospace' }}>#{suiteId}</span>
                                )}
                                <span style={{ fontSize: 11, fontWeight: 700, color: c, background: `${c}12`, padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap', flexShrink: 0 }}>{tc} TCs</span>
                                <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap', flexShrink: 0 }}>{st} steps</span>
                              </div>
                            );
                          })}
                          {/* Then render child folders */}
                          {childKeys.map(key => {
                            const child = node.children[key];
                            const folderPath = child.path || key;
                            const isExpanded = expandedFolders.has(folderPath);
                            const folderTCs = countTCs(child);
                            return (
                              <div key={folderPath}>
                                <div
                                    role="button"
                                    tabIndex={0}
                                  onClick={() => toggleFolder(folderPath)}
                                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleFolder(folderPath); } }}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: 6,
                                    padding: `6px 12px 6px ${12 + depth * 20}px`,
                                    background: depth % 2 === 0 ? '#f8fafc' : '#f1f5f9',
                                    borderBottom: '1px solid #e5e7eb',
                                    cursor: 'pointer', userSelect: 'none',
                                    transition: 'background 0.15s',
                                  }}
                                  onMouseEnter={e => { e.currentTarget.style.background = '#eef2ff'; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = depth % 2 === 0 ? '#f8fafc' : '#f1f5f9'; }}
                                >
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                                    style={{ flexShrink: 0, transition: 'transform 0.2s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                                    <polyline points="9 18 15 12 9 6"/>
                                  </svg>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill={isExpanded ? '#6366f1' : '#94a3b8'} stroke="none" style={{ flexShrink: 0 }}>
                                    <path d="M2 6a2 2 0 0 1 2-2h5l2 2h9a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6z"/>
                                  </svg>
                                  <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {child.name}
                                  </span>
                                  <span style={{
                                    fontSize: 10, fontWeight: 700, color: '#64748b',
                                    background: '#e2e8f0', padding: '1px 7px', borderRadius: 8,
                                    whiteSpace: 'nowrap', flexShrink: 0,
                                  }}>
                                    {folderTCs} TCs
                                  </span>
                                </div>
                                {isExpanded && renderNode(child, depth + 1)}
                              </div>
                            );
                          })}
                        </>
                      );
                    };

                    return renderNode(tree);
                  })()}
                </div>
              </>) : (<>
                {/* Single suite layout */}
                <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: 'var(--ro-text-primary)' }}>Test Suite Overview</h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, flex: 1, justifyContent: 'center' }}>
                  <div style={{ padding: '20px 16px', background: 'rgba(99,102,241,0.06)', borderRadius: 12, border: '1px solid rgba(99,102,241,0.12)' }}>
                    <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--ro-primary)', lineHeight: 1 }}>
                      {fileMetrics?.testCases ?? (summary?.original_count || '0')}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ro-text-muted)', marginTop: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Test Cases</div>
                  </div>
                  <div style={{ padding: '20px 16px', background: 'rgba(16,185,129,0.06)', borderRadius: 12, border: '1px solid rgba(16,185,129,0.12)' }}>
                    <div style={{ fontSize: 32, fontWeight: 800, color: '#10b981', lineHeight: 1 }}>
                      {fileMetrics?.totalSteps ?? (summary?.original_step_count || '0')}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ro-text-muted)', marginTop: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Steps</div>
                  </div>
                </div>

                {!loadedSuites.length && (
                  <p style={{ margin: '20px 0 0', fontSize: 13, color: 'var(--ro-text-muted)', textAlign: 'center' }}>
                    Upload a file or import from ADO to see metrics
                  </p>
                )}

                {loadedSuites.length > 0 && !fileMetrics && !isOptimizing && (
                  <p style={{ margin: '20px 0 0', fontSize: 13, color: 'var(--ro-text-muted)', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <span className="ro-btn-spinner" style={{ width: 12, height: 12, borderColor: 'rgba(99,102,241,0.3)', borderTopColor: '#6366f1' }}></span>
                    Analyzing test suite...
                  </p>
                )}

                {isOptimizing && !fileMetrics && (
                  <p style={{ margin: '20px 0 0', fontSize: 13, color: 'var(--ro-primary)', textAlign: 'center' }}>
                    <span className="ro-btn-spinner" style={{ width: 12, height: 12, marginRight: 6, verticalAlign: -1 }}></span>
                    Analyzing...
                  </p>
                )}
              </>)}

            </div>



            </div>

        </>)}

        {/* --- Domain Training Materials Popup (upload only, then return to landing) --- */}
        {trainingUploadDomain && !resultData && !pipelineVisible && !showConfirmStart && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, backdropFilter: 'blur(2px)' }}>
            <div style={{ background: '#fff', borderRadius: 16, padding: '32px 36px', maxWidth: 560, width: '92%', boxShadow: '0 24px 64px rgba(0,0,0,0.25)', animation: 'ro-result-fade-in 0.3s ease', position: 'relative' }}>
              <button
                onClick={() => { setTrainingUploadDomain(null); setShowConfirmStart(true); }}
                style={{ position: 'absolute', top: 16, right: 16, width: 32, height: 32, borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 16, color: '#64748b', lineHeight: 1 }}
                title="Close and continue"
              >
                {'\u2715'}
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
                <div style={{ width: 48, height: 48, borderRadius: 12, background: 'var(--ro-accent-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M12 2a10 10 0 1 0 10 10H12V2z"/><path d="M12 12 12 2a10 10 0 0 1 10 10z"/></svg>
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1e293b' }}>
                    {domainClassification?.domain_display_name || trainingUploadDomain.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </h3>
                  <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
                    Domain detected from your test suite
                  </p>
                </div>
              </div>

              <p style={{ margin: '0 0 16px', fontSize: 14, color: '#475569', lineHeight: 1.6 }}>
                For better optimization results, upload reference documents about this domain.
              </p>

              {isUploadingTraining ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '14px 20px', fontSize: 14, fontWeight: 600, borderRadius: 10, border: '2px dashed #94a3b8', background: '#f8fafc', color: '#64748b' }}>
                  <span className="ro-btn-spinner" style={{ width: 16, height: 16, borderColor: 'rgba(0,151,169,0.3)', borderTopColor: '#0097A9' }}></span>
                  Uploading...
                </div>
              ) : (
              <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '14px 20px', fontSize: 14, fontWeight: 700, borderRadius: 10, border: '2px dashed #0097A9', background: 'rgba(0,151,169,0.04)', color: '#0097A9', cursor: 'pointer', transition: 'all 0.15s ease' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,151,169,0.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,151,169,0.04)'; }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17,8 12,3 7,8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                Upload Reference Files (PDF, DOCX, TXT)
                <input type="file" accept=".pdf,.docx,.doc,.txt,.md" multiple style={{ display: 'none' }} onChange={async (e) => {
                  const files = e.target.files;
                  if (!files || files.length === 0) return;
                  setIsUploadingTraining(true);
                  try {
                    const names = [];
                    for (const f of files) {
                      await regressionOptimizerApi.uploadTraining(trainingUploadDomain, f);
                      names.push(f.name);
                    }
                    setSessionUploadedFiles(prev => [...prev, ...names]);
                    notify(`Uploaded ${files.length} file(s). You can add more or start optimization.`, 'success');
                    setTrainingUploadDomain(null);
                    setShowConfirmStart(true);
                  } catch (err) {
                    notify(err.message || 'Upload failed', 'error');
                  } finally {
                    setIsUploadingTraining(false);
                  }
                }} />
              </label>
              )}

              <button
                onClick={() => { setTrainingUploadDomain(null); setShowConfirmStart(true); }}
                style={{ marginTop: 12, width: '100%', padding: '12px 20px', fontSize: 14, fontWeight: 600, borderRadius: 10, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#64748b', cursor: 'pointer' }}
              >
                Skip -- Continue Without Uploading
              </button>

              <p style={{ margin: '14px 0 0', fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
                Files are stored and reused for all future optimizations of this domain.
              </p>
            </div>
          </div>
        )}

        {/* --- Confirm Start Section (on landing, after popup closed) --- */}
        {showConfirmStart && !resultData && !pipelineVisible && !trainingUploadDomain && (
          <div className="ro-glass-card" style={{ marginTop: 24, padding: '24px 28px', border: '1px solid rgba(0,151,169,0.2)', background: 'linear-gradient(135deg, rgba(0,151,169,0.02), rgba(1,66,106,0.01))' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--ro-accent-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M12 2a10 10 0 1 0 10 10H12V2z"/></svg>
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ro-text-primary)' }}>
                  {domainClassification?.domain_display_name || selectedDomainAgent?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Domain Detected'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--ro-text-muted)' }}>Ready to optimize with domain context</div>
              </div>
            </div>

            {/* Show uploaded training files as capsules */}
            {sessionUploadedFiles.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#00af66', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Training Reference Materials</span>
                <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {sessionUploadedFiles.map((name, i) => (
                    <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 12px', fontSize: 12, fontWeight: 600, borderRadius: 14, background: 'rgba(0,175,102,0.08)', color: '#059669', border: '1px solid rgba(0,175,102,0.2)' }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {sessionUploadedFiles.length === 0 && (
              <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--ro-text-muted)' }}>
                No additional training files added this session. Existing domain references (if any) will still be used.
              </p>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 4 }}>
              {/* Add more training files */}
              <label style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 16px', fontSize: 13, fontWeight: 600, borderRadius: 8, border: '1px solid var(--ro-accent-primary)', background: 'rgba(0,151,169,0.04)', color: 'var(--ro-accent-primary)', cursor: 'pointer', transition: 'all 0.15s ease' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17,8 12,3 7,8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                Add Training Files
                <input type="file" accept=".pdf,.docx,.doc,.txt,.md" multiple style={{ display: 'none' }} onChange={async (e) => {
                  const files = e.target.files;
                  if (!files || files.length === 0) return;
                  const domain = selectedDomainAgent || domainClassification?.domain_agent;
                  if (!domain) return;
                  try {
                    const names = [];
                    for (const f of files) {
                      await regressionOptimizerApi.uploadTraining(domain, f);
                      names.push(f.name);
                    }
                    setSessionUploadedFiles(prev => [...prev, ...names]);
                    notify(`Added ${files.length} file(s)`, 'success');
                  } catch (err) {
                    notify(err.message || 'Upload failed', 'error');
                  }
                }} />
              </label>

              {/* Start Optimization button */}
              <button
                onClick={() => {
                  skipTrainingRef.current = true;
                  runOptimize();
                }}
                style={{ padding: '11px 24px', fontSize: 13, fontWeight: 700, borderRadius: 8, border: 'none', background: 'var(--ro-accent-gradient)', color: '#fff', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,151,169,0.3)', minWidth: 220, textAlign: 'center', transition: 'all 0.15s ease' }}
              >
                {sessionUploadedFiles.length > 0 ? 'Start Optimization with Context' : 'Start Optimization'}
              </button>
            </div>
          </div>
        )}

        {/* --- Domain Agent Card (above pipeline) --- */}
        {resultData?.domain_agent && (
          <div className="ro-glass-card" style={{ marginTop: 24, padding: '20px 24px', border: '1px solid rgba(0,151,169,0.2)', background: 'linear-gradient(135deg, rgba(0,151,169,0.03), rgba(1,66,106,0.02))' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--ro-accent-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M12 2a10 10 0 1 0 10 10H12V2z"/><path d="M12 12 12 2a10 10 0 0 1 10 10z"/></svg>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ro-text-primary)' }}>
                  Domain Agent: {resultData.domain_display_name || resultData.domain_agent}
                </div>
                <div style={{ fontSize: 12, color: 'var(--ro-text-muted)', marginTop: 2 }}>Domain-specific optimization applied</div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                {sessionUploadedFiles.length > 0 && (
                  <button onClick={() => setShowTrainingFiles(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', fontSize: 12, fontWeight: 600, borderRadius: 8, border: '1px solid rgba(0,151,169,0.2)', background: 'rgba(0,151,169,0.04)', color: 'var(--ro-accent-primary)', cursor: 'pointer' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
                    Training References ({sessionUploadedFiles.length})
                  </button>
                )}
                <button onClick={() => setShowOriginalFile(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', fontSize: 12, fontWeight: 600, borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: 'var(--ro-text-secondary)', cursor: 'pointer' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  View Original Suite
                </button>
              </div>
            </div>
          </div>
        )}

        {/* --- Pipeline Progress -- */}

        {pipelineVisible && (

          <div className="ro-pipeline-section ro-glass-card ro-pipeline-enhanced" style={{ marginTop: 24 }}>

            <div className="ro-pipeline-header">

              <div className="ro-pipeline-icon">

                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">

                  <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>

                </svg>

              </div>

              <div style={{ flex: 1 }}>

                <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--ro-text-primary)' }}>Optimization Pipeline</h3>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span className="ro-pipeline-status" style={{ fontSize: 14 }}>{pipelineStatus}</span>
                  {(resultData?.domain_agent || selectedDomainAgent) && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', fontSize: 11, fontWeight: 700, borderRadius: 12, background: 'rgba(0,151,169,0.1)', color: 'var(--ro-accent-primary)', border: '1px solid rgba(0,151,169,0.2)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2a10 10 0 1 0 10 10H12V2z"/></svg>
                      {resultData?.domain_display_name || resultData?.classification?.suggested_agents?.[0]?.display_name || domainClassification?.domain_display_name || selectedDomainAgent}
                    </span>
                  )}
                </div>

              </div>

              {isOptimizing && (

                <div className="ro-pipeline-timer">

                  <span className="ro-timer-elapsed">{formatElapsed(elapsedSec)}</span>

                  <span className="ro-timer-estimate">{getEstimatedRemaining()}</span>

                </div>

              )}

            </div>



            <div className="ro-pipeline-visualization ro-pipeline-vis-enhanced">

              <div className="ro-pipeline-stages-container ro-stages-enhanced">

                {PIPELINE_STAGES.map((s, idx) => {

                  // Get dynamic result text from stages_status when optimization is complete
                  const stageResult = summary?.stages_status?.[s.statusKey];
                  const resultMsg = stageResult && typeof stageResult === 'object' ? stageResult.message : '';
                  const embedMethod = stageResult && typeof stageResult === 'object' ? stageResult.embedding_method : '';

                  return (

                  <React.Fragment key={s.key}>

                    <div className={`ro-pipeline-stage ro-stage-large ${stageStates[s.key] || ''}`} data-stage={s.key}>

                      <div className="ro-stage-icon-large">

                        {s.icon}

                      </div>

                      <div className="ro-stage-info-large">

                        <span className="ro-stage-number-large">Stage {s.number}</span>

                        <span className="ro-stage-name-large">{s.label}</span>

                      </div>

                      {/* Dynamic result below stage when completed */}
                      {stageStates[s.key] === 'completed' && resultMsg && (
                        <div className="ro-stage-result">
                          {resultMsg}
                        </div>
                      )}

                      {/* Tooltip on hover */}

                      <div className="ro-stage-tooltip">{s.tooltip}</div>

                    </div>

                    {idx < PIPELINE_STAGES.length - 1 && (

                      <div className={`ro-pipeline-connector-large ${

                        stageStates[s.key] === 'completed' ? 'completed' : ''

                      }`}>

                        <svg viewBox="0 0 24 12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M0 6h24"/><path d="M18 1l5 5-5 5"/></svg>

                      </div>

                    )}

                  </React.Fragment>

                  );

                })}

              </div>



              <div className="ro-overall-progress ro-progress-enhanced">

                <div className="ro-progress-bar">

                  <div className="ro-progress-fill" style={{ width: `${progressPct}%` }}></div>

                </div>

                <span className="ro-progress-text">{progressPct}%</span>

              </div>

            </div>

          </div>

        )}



        {/* --- Results Dashboard -- */}

        {resultData && summary && (

          <section style={{ marginTop: 24 }}>

            {/* View Original button when no domain agent */}
            {!resultData?.domain_agent && originalData && (
              <div style={{ marginBottom: 16, textAlign: 'right' }}>
                <button
                  onClick={() => setShowOriginalFile(true)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: 'var(--ro-text-secondary)', cursor: 'pointer' }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  View Original Suite
                </button>
              </div>
            )}

            {/* --- Data Tabs -- */}

            <div className="ro-glass-card" style={{ marginTop: 0 }}>

              <div className="ro-tabs-header">

                <button className={`ro-tab ${activeTab === 'diff' ? 'active' : ''}`} onClick={() => setActiveTab('diff')}>

                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M16 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8z"/><path d="M16 3v5h5"/></svg>

                  Imported Test Cases <span className="ro-tab-count">{summary?.original_count || 0}</span>

                </button>

                <button className={`ro-tab ${activeTab === 'optimized' ? 'active' : ''}`} onClick={() => setActiveTab('optimized')}>

                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="M22 4L12 14.01l-3-3"/></svg>

                  Retained Test Cases <span className="ro-tab-count">{summary.optimized_count}</span>

                </button>

                <button className={`ro-tab ${activeTab === 'removed' ? 'active' : ''}`} onClick={() => setActiveTab('removed')}>

                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>

                  Recommended Removals <span className="ro-tab-count">{removed.length}</span>

                </button>

                <button className={`ro-tab ${activeTab === 'recommendations' ? 'active' : ''}`} onClick={() => setActiveTab('recommendations')}>

                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M16 13H8"/><path d="M16 17H8"/><path d="M10 9H8"/></svg>

                  Recommended Concatenations <span className="ro-tab-count">{recommendations.length}</span>

                </button>

              </div>



              {/* --- Diff Tab -- */}

              <div className={`ro-tab-pane ${activeTab === 'diff' ? 'active' : ''}`}>

                {originalData && originalData.rows?.length > 0 ? (() => {

                  const diffHasSourceSuite = originalData.columns.includes('__source_suite__');
                  const diffCols = originalData.columns.filter(c => c !== '__source_suite__');

                  return (

                  <div className="ro-data-table-wrapper">

                    <table className="ro-data-table ro-data-table-wide">

                      <thead>

                        <tr>

                          <th className="ro-col-status" style={getColumnStyle('Status')}>

                            <span className="ro-header-label">Status</span>

                            {renderColumnResizer('Status')}

                          </th>

                          {diffHasSourceSuite && (
                            <th className="ro-col-status" style={getColumnStyle('Source Suite')}>
                              <span className="ro-header-label">Source Suite</span>
                              {renderColumnResizer('Source Suite')}
                            </th>
                          )}

                          {diffCols.map(c => (

                            isStepCol(c)

                              ? <th key={c} className={`col-toggle ${getColumnClass(c, originalData.id_column)}`} style={getColumnStyle(c)} onClick={() => toggleCol(c)}>

                                  <span className="ro-header-label">{expandedCols.has(c) ? '▼ ' : '▶ '}{c}</span>

                                  {renderColumnResizer(c)}

                                </th>

                              : <th key={c} className={getColumnClass(c, originalData.id_column)} style={getColumnStyle(c)}>

                                  <span className="ro-header-label">{c}</span>

                                  {renderColumnResizer(c)}

                                </th>

                          ))}

                          <th className="ro-col-reason" style={getColumnStyle('Reason')}>

                            <span className="ro-header-label">Reason</span>

                            {renderColumnResizer('Reason')}

                          </th>

                        </tr>

                      </thead>

                      <tbody>

                        {originalData.rows.map((row, idx) => {

                          const tcId = String(row[originalData.tc_id_field] || '').trim();

                          const isRemoved = tcId in removedMap;

                          return (

                            <tr key={idx} className={isRemoved ? 'row-removed' : 'row-kept'}>

                              <td style={getColumnStyle('Status')}>

                                <span className={`ro-badge ${isRemoved ? 'ro-badge-removed' : 'ro-badge-kept'}`}>

                                  {isRemoved ? 'Removed' : 'Kept'}

                                </span>

                              </td>

                              {diffHasSourceSuite && (
                                <td style={getColumnStyle('Source Suite')}>
                                  <span className="ro-badge" style={{ background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe', fontSize: 11, padding: '2px 7px' }}>{row['__source_suite__'] || ''}</span>
                                </td>
                              )}

                              {diffCols.map(c => (

                                isStepCol(c)

                                  ? <td key={c} style={getColumnStyle(c)} className={`${getColumnClass(c, originalData.id_column)} ${expandedCols.has(c) ? '' : 'col-hidden'}`.trim()}>{formatCellValue(row[c])}</td>

                                  : <td key={c} style={getColumnStyle(c)} className={`${getColumnClass(c, originalData.id_column)} ${c === originalData.id_column ? 'ro-id-cell' : ''}`.trim()}>{formatCellValue(row[c])}</td>

                              ))}

                              <td className="reason-text" style={getColumnStyle('Reason')}>{isRemoved ? removedMap[tcId] : ''}</td>

                            </tr>

                          );

                        })}

                      </tbody>

                    </table>

                  </div>

                  );

                })() : (

                  <div className="ro-empty-state"><p>No data for diff view.</p></div>

                )}

              </div>



              {/* --- Optimized Tab -- */}

              <div className={`ro-tab-pane ${activeTab === 'optimized' ? 'active' : ''}`}>

                {originalData && originalData.rows?.length > 0 ? (() => {

                  const optHasSourceSuite = originalData.columns.includes('__source_suite__');
                  const optCols = originalData.columns.filter(c => c !== '__source_suite__');

                  return (

                  <div className="ro-data-table-wrapper">

                    <table className="ro-data-table ro-data-table-wide">

                      <thead>

                        <tr>

                          {optHasSourceSuite && (
                            <th className="ro-col-status" style={getColumnStyle('Source Suite')}>
                              <span className="ro-header-label">Source Suite</span>
                              {renderColumnResizer('Source Suite')}
                            </th>
                          )}

                          {optCols.map(c => (

                            isStepCol(c)

                              ? <th key={c} className={`col-toggle ${getColumnClass(c, originalData.id_column)}`} style={getColumnStyle(c)} onClick={() => toggleCol(c)}>

                                  <span className="ro-header-label">{expandedCols.has(c) ? '▼ ' : '▶ '}{c}</span>

                                  {renderColumnResizer(c)}

                                </th>

                              : <th key={c} className={getColumnClass(c, originalData.id_column)} style={getColumnStyle(c)}>

                                  <span className="ro-header-label">{c}</span>

                                  {renderColumnResizer(c)}

                                </th>

                          ))}

                        </tr>

                      </thead>

                      <tbody>

                        {originalData.rows.filter(row => {

                          const tcId = String(row[originalData.tc_id_field] || '').trim();

                          return !(tcId in removedMap);

                        }).map((row, idx) => (

                          <tr key={idx} className="row-kept">

                            {optHasSourceSuite && (
                              <td style={getColumnStyle('Source Suite')}>
                                <span className="ro-badge" style={{ background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe', fontSize: 11, padding: '2px 7px' }}>{row['__source_suite__'] || ''}</span>
                              </td>
                            )}

                            {optCols.map(c => (

                              isStepCol(c)

                                ? <td key={c} style={getColumnStyle(c)} className={`${getColumnClass(c, originalData.id_column)} ${expandedCols.has(c) ? '' : 'col-hidden'}`.trim()}>{formatCellValue(row[c])}</td>

                                : <td key={c} style={getColumnStyle(c)} className={`${getColumnClass(c, originalData.id_column)} ${c === originalData.id_column ? 'ro-id-cell' : ''}`.trim()}>{formatCellValue(row[c])}</td>

                            ))}

                          </tr>

                        ))}

                      </tbody>

                    </table>

                  </div>

                  );

                })() : (

                  <div className="ro-empty-state"><p>Download the Excel to see the full optimized suite.</p></div>

                )}

              </div>



              {/* --- Removed Tab (all original columns) -- */}

              <div className={`ro-tab-pane ${activeTab === 'removed' ? 'active' : ''}`}>

                {removed.length > 0 ? (() => {

                  const removedCols = Object.keys(removed[0]).filter(c => c !== 'Removal Reason' && c !== '__source_suite__');

                  const hasSourceSuite = '__source_suite__' in removed[0];

                  return (

                    <div className="ro-data-table-wrapper">

                      <table className="ro-data-table ro-data-table-wide">

                        <thead>

                          <tr>

                            {hasSourceSuite && (
                              <th className="ro-col-status" style={getColumnStyle('Source Suite')}>
                                <span className="ro-header-label">Source Suite</span>
                                {renderColumnResizer('Source Suite')}
                              </th>
                            )}

                            {removedCols.map(c => (

                              <th key={c} className={getColumnClass(c, idColumn)} style={getColumnStyle(c)}>

                                <span className="ro-header-label">{c}</span>

                                {renderColumnResizer(c)}

                              </th>

                            ))}

                            <th className="ro-col-reason" style={getColumnStyle('Removal Reason')}>

                              <span className="ro-header-label">Removal Reason</span>

                              {renderColumnResizer('Removal Reason')}

                            </th>

                          </tr>

                        </thead>

                        <tbody>

                          {removed.map((r, idx) => (

                            <tr key={idx} className="row-removed">

                              {hasSourceSuite && (
                                <td style={getColumnStyle('Source Suite')}>
                                  <span className="ro-badge" style={{ background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe', fontSize: 11, padding: '2px 7px' }}>{r['__source_suite__'] || ''}</span>
                                </td>
                              )}

                              {removedCols.map(c => <td key={c} style={getColumnStyle(c)} className={`${getColumnClass(c, idColumn)} ${c === idColumn ? 'ro-id-cell' : ''}`.trim()}>{formatCellValue(r[c])}</td>)}

                              <td className="reason-text" style={getColumnStyle('Removal Reason')}>{r['Removal Reason'] || ''}</td>

                            </tr>

                          ))}

                        </tbody>

                      </table>

                    </div>

                  );

                })() : (

                  <div className="ro-empty-state"><p>No test cases were removed.</p></div>

                )}

              </div>



              {/* --- Recommendations Tab (all 9 columns) -- */}

              <div className={`ro-tab-pane ${activeTab === 'recommendations' ? 'active' : ''}`}>

                {recommendations.length > 0 ? (

                  <>

                    <div className="ro-data-table-wrapper">

                      <table className="ro-data-table ro-data-table-wide ro-recommendations-table">

                        <thead>

                          <tr>

                            <th className="ro-col-status" style={getColumnStyle('Type')}><span className="ro-header-label">Type</span>{renderColumnResizer('Type')}</th>

                            <th className="ro-col-status" style={getColumnStyle('Source Suite')}><span className="ro-header-label">Source Suite</span>{renderColumnResizer('Source Suite')}</th>

                            <th className="ro-col-title" style={getColumnStyle('Feature')}><span className="ro-header-label">Feature</span>{renderColumnResizer('Feature')}</th>

                            <th className="ro-col-step-action" style={getColumnStyle('Action')}><span className="ro-header-label">Action</span>{renderColumnResizer('Action')}</th>

                            <th className="ro-col-step-expected" style={getColumnStyle('Assertion')}><span className="ro-header-label">Assertion</span>{renderColumnResizer('Assertion')}</th>

                            <th className="ro-col-id" style={getColumnStyle('Source TCs')}><span className="ro-header-label">Source TCs</span>{renderColumnResizer('Source TCs')}</th>

                            <th className="ro-col-id" style={getColumnStyle('Keep')}><span className="ro-header-label">Keep</span>{renderColumnResizer('Keep')}</th>

                            <th className="ro-col-reason" style={getColumnStyle('Reason')}><span className="ro-header-label">Reason</span>{renderColumnResizer('Reason')}</th>

                            <th className="ro-col-step-action" style={getColumnStyle('Parameters')}><span className="ro-header-label">Parameters</span>{renderColumnResizer('Parameters')}</th>

                            <th className="ro-col-step-expected" style={getColumnStyle('Assertions Covered')}><span className="ro-header-label">Assertions Covered</span>{renderColumnResizer('Assertions Covered')}</th>

                            <th className="ro-col-step-action" style={getColumnStyle('BDD Scenario')}><span className="ro-header-label">BDD Scenario</span>{renderColumnResizer('BDD Scenario')}</th>

                          </tr>

                        </thead>

                        <tbody>

                          {recommendations.map((rec, idx) => {

                            const intent = rec.intent || {};

                            const badge = rec.type === 'redundancy'

                              ? <span className="ro-badge ro-badge-removed">Redundancy</span>

                              : rec.type === 'parameterize'

                              ? <span className="ro-badge ro-badge-parameterize">Parameterize</span>

                              : <span className="ro-badge ro-badge-club">Club/Merge</span>;

                            const sourceSuites = [...new Set((rec.source_tc_ids || []).map(tcId => {
                              const row = findTcById(tcId);
                              return row?.['__source_suite__'] || '';
                            }).filter(Boolean))];

                            return (

                              <tr key={idx}>

                                <td className="ro-col-status" style={getColumnStyle('Type')}>{badge}</td>

                                <td className="ro-col-status" style={getColumnStyle('Source Suite')}>
                                  {sourceSuites.map((s, si) => (
                                    <span key={si} className="ro-badge" style={{ background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe', fontSize: 11, padding: '2px 7px', marginRight: 4 }}>{s}</span>
                                  ))}
                                </td>

                                <td className="ro-col-title" style={getColumnStyle('Feature')}>{formatCellValue(intent.feature || '')}</td>

                                <td className="ro-col-step-action" style={getColumnStyle('Action')}>{formatCellValue(intent.action || '')}</td>

                                <td className="ro-col-step-expected" style={getColumnStyle('Assertion')}>{formatCellValue(intent.assertion || '')}</td>

                                <td className="ro-col-id" style={getColumnStyle('Source TCs')}>{(rec.source_tc_ids || []).join(', ')}</td>

                                <td className="ro-col-id" style={getColumnStyle('Keep')}>{rec.keep_tc_id || '—'}</td>

                                <td className="reason-text" style={getColumnStyle('Reason')}>{rec.reason || ''}</td>

                                <td className="ro-col-step-action" style={{ ...getColumnStyle('Parameters'), fontSize: 11 }}>

                                  {(rec.parameter_table || []).map((p, pi) => (

                                    <div key={pi}>{JSON.stringify(p)}</div>

                                  ))}

                                </td>

                                <td className="ro-col-step-expected" style={{ ...getColumnStyle('Assertions Covered'), fontSize: 11 }}>

                                  {(rec.assertions_covered || []).map((a, ai) => (

                                    <div key={ai}>{a}</div>

                                  ))}

                                </td>

                                <td className="ro-col-step-action" style={{ ...getColumnStyle('BDD Scenario'), whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 11 }}>

                                  {rec.bdd_scenario || '—'}

                                </td>

                              </tr>

                            );

                          })}

                        </tbody>

                      </table>

                    </div>

                  </>

                ) : (

                  <div className="ro-empty-state"><p>No merge / parameterize recommendations.</p></div>

                )}

              </div>

            </div>

            {/* -- Review Section (matches standalone -- separate card, scrolls into view) -- */}

            {reviewVisible && (

              <div className="ro-review-section">

                <div className="ro-glass-card ro-review-card" style={{ marginTop: 24 }}>

                  <div className="ro-review-card-header">

                    <div className="ro-review-card-icon">

                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">

                        <path d="M9 11l3 3L22 4"/>

                        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>

                      </svg>

                    </div>

                    <div>

                      <div className="ro-review-card-title">Review Proposed Changes</div>

                      <div className="ro-review-card-subtitle">Accept or reject suggestions, then download the optimized Excel or push directly to ADO</div>

                    </div>

                  </div>



                  <div className="ro-review-actions">

                    <button className="accept-all" onClick={acceptAll}>

                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M20 6L9 17l-5-5"/></svg>

                      Accept All

                    </button>

                    <button className="reject-all" onClick={rejectAll}>

                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>

                      Reject All

                    </button>

                  </div>



                  {/* Removals group */}

                  {removed.length > 0 && (

                    <div className="ro-review-group">

                      <div className="ro-review-group-header">

                        <h3>

                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>

                          Proposed Removals

                        </h3>

                        <span className="ro-count-badge">{removed.length}</span>

                      </div>

                      <div className="ro-review-list">

                        {removed.map((r, idx) => {

                          const tcId = String(r[idColumn] || '').trim();

                          const decision = reviewDecisions.removals[tcId] || 'pending';

                          const reason = r['Removal Reason'] || 'Similar to another test case';

                          const similarTcId = extractSimilarTcId(reason);

                          const similarTcRow = findTcById(similarTcId);

                          const isExpanded = expandedReviewItems.has(`removal-${idx}`);

                          const removedFields = getDisplayFields(r);

                          const similarFields = getDisplayFields(similarTcRow);

                          const sourceSuite = r['__source_suite__'] || '';

                          return (

                            <div key={idx} className={`ro-review-item-wrapper ${isExpanded ? 'expanded' : ''}`}>

                              <div className={`ro-review-item ${decision === 'accepted' ? 'review-accepted' : decision === 'rejected' ? 'review-rejected' : ''}`}>

                                <div className="ro-review-item-info">

                                  <span className="review-tc-id">
                                    {sourceSuite && <span style={{ background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe', fontSize: 10, padding: '1px 6px', borderRadius: 10, marginRight: 6, fontWeight: 600 }}>{sourceSuite}</span>}
                                    {tcId || `Row ${idx + 1}`}{similarTcId ? ` & ${similarTcId}` : ''} - Similar Test Cases
                                  </span>

                                  <span className="review-reason">{reason}</span>

                                </div>

                                <div className="ro-review-item-actions">

                                  {similarTcId && (

                                    <button

                                      className={`review-item-toggle ro-compare-btn${isExpanded ? ' active' : ''}`}

                                      onClick={() => toggleReviewExpand(`removal-${idx}`)}

                                      title="Compare both test cases"

                                    >

                                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">

                                        <path d="M16 3h5v5"/><path d="M8 21H3v-5"/><path d="M21 3l-8.5 8.5"/><path d="M3 21l8.5-8.5"/>

                                      </svg>

                                      Compare

                                    </button>

                                  )}

                                  <button

                                    className={`review-item-toggle${decision === 'accepted' ? ' active' : ''}`}

                                    onClick={() => setRemovalDecision(tcId, 'accepted')}

                                  >Remove {tcId}</button>

                                  {similarTcId && (

                                    <button

                                      className={`review-item-toggle${decision === 'accepted-swap' ? ' active' : ''}`}

                                      onClick={() => setRemovalDecision(tcId, 'accepted-swap')}

                                    >Remove {similarTcId}</button>

                                  )}

                                  <button

                                    className={`review-item-toggle${decision === 'rejected' ? ' active' : ''}`}

                                    onClick={() => setRemovalDecision(tcId, 'rejected')}

                                  >Keep Both</button>

                                </div>

                              </div>

                              {decision === 'rejected' && !feedbackConfirmed[`removal-${tcId}`] && (

                                <div className="ro-reject-feedback-inline">

                                  <input

                                    type="text"

                                    className="ro-reject-feedback-input"

                                    placeholder="Why keep both? (optional, helps improve future suggestions)"

                                    value={rejectFeedback[`removal-${tcId}`] || ''}

                                    onChange={(e) => setRejectFeedback(prev => ({ ...prev, [`removal-${tcId}`]: e.target.value }))}

                                  />

                                  <button

                                    className="ro-reject-feedback-confirm"

                                    title="Confirm feedback"

                                    onClick={() => setFeedbackConfirmed(prev => ({ ...prev, [`removal-${tcId}`]: true }))}

                                  >

                                    {'\u2713'}

                                  </button>

                                </div>

                              )}

                              {decision === 'rejected' && feedbackConfirmed[`removal-${tcId}`] && rejectFeedback[`removal-${tcId}`] && (

                                <div className="ro-reject-feedback-confirmed">

                                  Feedback saved: {rejectFeedback[`removal-${tcId}`]}

                                </div>

                              )}

                              {/* --- Expanded comparison panel (tabular) -- */}

                              {isExpanded && (

                                <div className="ro-comparison-panel">

                                  <table className="ro-comparison-table">

                                    <thead>

                                      <tr>

                                        <th>Field</th>

                                        <th>TC {tcId}</th>

                                        <th>TC {similarTcId}</th>

                                      </tr>

                                    </thead>

                                    <tbody>

                                      {(() => {

                                        const mergedFieldLabels = [];

                                        const labelByKey = new Map();



                                        const addFieldLabel = (field) => {

                                          const key = normalizeFieldLabel(field.label);

                                          if (!key) return;

                                          if (!labelByKey.has(key)) {

                                            labelByKey.set(key, field.label);

                                            mergedFieldLabels.push(key);

                                          }

                                        };



                                        removedFields.forEach(addFieldLabel);

                                        similarFields.forEach(addFieldLabel);



                                        const removedFieldMap = Object.fromEntries(removedFields.map(f => [normalizeFieldLabel(f.label), f.value]));

                                        const similarFieldMap = Object.fromEntries(similarFields.map(f => [normalizeFieldLabel(f.label), f.value]));



                                        return mergedFieldLabels.map(fieldKey => {

                                          const label = labelByKey.get(fieldKey);

                                          return (

                                            <tr key={fieldKey}>

                                              <td className="ro-comparison-table-label">{label}</td>

                                              <td className="ro-comparison-table-value">{fieldKey in removedFieldMap ? renderComparisonValue(label, removedFieldMap[fieldKey]) : '--'}</td>

                                              <td className="ro-comparison-table-value">{fieldKey in similarFieldMap ? renderComparisonValue(label, similarFieldMap[fieldKey]) : '--'}</td>

                                            </tr>

                                          );

                                        });

                                      })()}

                                    </tbody>

                                  </table>

                                </div>

                              )}

                            </div>

                          );

                        })}

                      </div>

                    </div>

                  )}



                  {/* Recommendations group */}

                  {recommendations.length > 0 && (

                    <div className="ro-review-group">

                      <div className="ro-review-group-header">

                        <h3>

                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>

                          Merge / Parameterize Recommendations

                        </h3>

                        <span className="ro-count-badge">{recommendations.length}</span>

                      </div>

                      <div className="ro-review-list">

                        {recommendations.map((rec, idx) => {

                          const decision = reviewDecisions.recommendations[idx] || 'pending';

                          const badge = rec.type === 'redundancy'

                            ? <span className="ro-badge ro-badge-removed">Redundancy</span>

                            : rec.type === 'parameterize'

                            ? <span className="ro-badge ro-badge-parameterize">Parameterize</span>

                            : <span className="ro-badge ro-badge-club">Club/Merge</span>;

                          const recSourceSuites = [...new Set((rec.source_tc_ids || []).map(tcId => {
                            const row = findTcById(tcId);
                            return row?.['__source_suite__'] || '';
                          }).filter(Boolean))];

                          return (

                            <div key={idx} className={`ro-review-item-wrapper ${decision === 'rejected' ? 'has-feedback' : ''}`}>

                              <div className={`ro-review-item ${decision === 'accepted' ? 'review-accepted' : decision === 'rejected' ? 'review-rejected' : ''}`}>

                              <div className="ro-review-item-info">

                                <span className="review-tc-id">

                                  {badge}

                                  {recSourceSuites.length > 0 && recSourceSuites.map((s, si) => (
                                    <span key={si} style={{ background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe', fontSize: 10, padding: '1px 6px', borderRadius: 10, marginLeft: 4, marginRight: 2, fontWeight: 600 }}>{s}</span>
                                  ))}

                                  {' '}Sources: {(rec.source_tc_ids || []).join(', ')}

                                  {rec.keep_tc_id && <span className="review-keep"> | Keep: {rec.keep_tc_id}</span>}

                                </span>

                                <span className="review-reason">{rec.reason || ''}</span>

                              </div>

                              <div className="ro-review-item-actions">

                                <button

                                  className={`review-item-toggle${decision === 'accepted' ? ' active' : ''}`}

                                  onClick={() => setRecommendationDecision(idx, 'accepted')}

                                >Accept</button>

                                <button

                                  className={`review-item-toggle${decision === 'rejected' ? ' active' : ''}`}

                                  onClick={() => setRecommendationDecision(idx, 'rejected')}

                                >Reject</button>

                              </div>

                              </div>

                              {decision === 'rejected' && !feedbackConfirmed[`rec-${idx}`] && (

                                <div className="ro-reject-feedback-inline">

                                  <input

                                    type="text"

                                    className="ro-reject-feedback-input"

                                    placeholder="Why did you reject? (optional, helps improve future suggestions)"

                                    value={rejectFeedback[`rec-${idx}`] || ''}

                                    onChange={(e) => setRejectFeedback(prev => ({ ...prev, [`rec-${idx}`]: e.target.value }))}

                                  />

                                  <button

                                    className="ro-reject-feedback-confirm"

                                    title="Confirm feedback"

                                    onClick={() => setFeedbackConfirmed(prev => ({ ...prev, [`rec-${idx}`]: true }))}

                                  >

                                    {'\u2713'}

                                  </button>

                                </div>

                              )}

                              {decision === 'rejected' && feedbackConfirmed[`rec-${idx}`] && rejectFeedback[`rec-${idx}`] && (

                                <div className="ro-reject-feedback-confirmed">

                                  Feedback saved: {rejectFeedback[`rec-${idx}`]}

                                </div>

                              )}

                            </div>

                          );

                        })}

                      </div>

                    </div>

                  )}



                  {/* Review Summary -- two lines matching standalone */}

                  <div className="ro-review-summary">

                    <div className="ro-review-summary-grid">

                      <div className="ro-review-summary-item">

                        <span className="ro-review-summary-label">Removals:</span>{' '}

                        <span className="ro-review-summary-accepted">{removalCounts.accepted} accepted</span>{' '}

                        <span className="ro-review-summary-rejected">{removalCounts.rejected} rejected</span>

                        {removalCounts.pending > 0 && <span className="ro-review-summary-pending"> {removalCounts.pending} pending</span>}

                      </div>

                      <div className="ro-review-summary-item">

                        <span className="ro-review-summary-label">Recommendations:</span>{' '}

                        <span className="ro-review-summary-accepted">{recCounts.accepted} accepted</span>{' '}

                        <span className="ro-review-summary-rejected">{recCounts.rejected} rejected</span>

                        {recCounts.pending > 0 && <span className="ro-review-summary-pending"> {recCounts.pending} pending</span>}

                      </div>

                    </div>

                  </div>



                  {/* Output Format Dialog */}

                  {showFormatDialog && (

                    <div className="ro-format-dialog-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>

                      <div style={{ background: '#fff', borderRadius: 12, padding: '28px 32px', maxWidth: 480, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>

                        <h3 style={{ margin: '0 0 8px', fontSize: '1.1rem' }}>Choose Output Format</h3>

                        <p style={{ margin: '0 0 20px', color: '#555', fontSize: '0.88rem' }}>How would you like the test cases formatted in the downloaded Excel?</p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

                          <button

                            onClick={() => runFinalize('bdd')}

                            style={{ padding: '14px 18px', borderRadius: 8, border: '1px solid #6366f1', background: 'rgba(99,102,241,0.06)', cursor: 'pointer', textAlign: 'left' }}

                          >

                            <strong style={{ color: '#4f46e5' }}>BDD Format (Gherkin)</strong>

                            <div style={{ fontSize: '0.82rem', color: '#555', marginTop: 4 }}>All test cases converted to Step 1, Step 2... format with Given/When/Then. Ready for ADO or Jira.</div>

                          </button>

                          <button

                            onClick={() => runFinalize('steps')}

                            style={{ padding: '14px 18px', borderRadius: 8, border: '1px solid #10b981', background: 'rgba(16,185,129,0.06)', cursor: 'pointer', textAlign: 'left' }}

                          >

                            <strong style={{ color: '#059669' }}>Steps + Parameter Table</strong>

                            <div style={{ fontSize: '0.82rem', color: '#555', marginTop: 4 }}>Original step format preserved. Parameterized values shown in a clear table.</div>

                          </button>

                        </div>

                        <button onClick={() => setShowFormatDialog(false)} style={{ marginTop: 16, padding: '8px 16px', border: 'none', background: '#f3f4f6', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem', color: '#666' }}>Cancel</button>

                      </div>

                    </div>

                  )}

                </div>

              </div>

            )}




            {/* --- Metrics Dashboard Card --- */}
            <div className="ro-glass-card" style={{ marginTop: 24, padding: '28px 24px' }}>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 24 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M3 3h18v18H3z"/><path d="M3 9h18"/><path d="M3 15h18"/><path d="M9 3v18"/></svg>
              </div>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ro-text-primary)' }}>Optimization Metrics & Impact</div>
                <div style={{ fontSize: 13, color: 'var(--ro-text-muted)', marginTop: 2 }}>Detailed breakdown of test suite analysis, optimization progress, and estimated cost savings</div>
              </div>
            </div>

            {/* --- Row 1: Static Metrics (don't change after optimization) -- */}

            <div style={{ marginBottom: 20 }}>

              <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 14, color: 'var(--ro-text-primary)' }}>Input Analysis</h4>

              <div className="ro-metrics-row ro-metrics-static">

                <div className="ro-metric-card ro-metric-purple">

                  <div className="ro-metric-value">{fileMetrics?.testCases ?? summary.original_count}</div>

                  <div className="ro-metric-label">Test Cases</div>

                </div>

                <div className="ro-metric-card ro-metric-green">

                  <div className="ro-metric-value">{fileMetrics?.totalSteps ?? (summary.original_step_count || 0)}</div>

                  <div className="ro-metric-label">Total Steps</div>

                </div>

                <div className="ro-metric-card ro-metric-indigo">

                  <div className="ro-metric-value">{removed.length + recommendations.length}</div>

                  <div className="ro-metric-label">Total Suggestions</div>

                </div>

                <div className="ro-metric-card ro-metric-red">

                  <div className="ro-metric-value">{removed.length}</div>

                  <div className="ro-metric-label">Potential Duplicates</div>

                </div>

                <div className="ro-metric-card ro-metric-emerald">

                  <div className="ro-metric-value">{recommendations.length}</div>

                  <div className="ro-metric-label">Parameterisations</div>

                </div>

                {/* Token Usage Card */}

                <div className="ro-metric-card ro-metric-cyan">

                  <div className="ro-metric-value">{(resultData?.token_usage?.total_tokens || 0).toLocaleString()}</div>

                  <div className="ro-metric-label">Tokens Used</div>

                  <div className="ro-metric-sub">

                    {resultData?.token_usage?.calls || 0} API calls

                  </div>

                </div>

              </div>

            </div>



            {/* --- Row 2: Dynamic Metrics (update as user accepts/rejects) -- */}

            <div className="ro-stats-section">

              <h4 className="ro-stats-section-title" style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, color: 'var(--ro-text-primary)' }}>Optimization Impact <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--ro-text-muted)' }}>-- updates as you review</span></h4>

              <div className="ro-metrics-row ro-metrics-dynamic">

                <div className="ro-metric-card ro-metric-orange">

                  <div className="ro-metric-value">

                    {recommendations.reduce((sum, rec) => sum + (rec.source_tc_ids || []).length, 0)}

                  </div>

                  <div className="ro-metric-label">Optimisable TCs</div>

                </div>

                <div className="ro-metric-card ro-metric-lime">

                  <div className="ro-metric-value">

                    {recommendations.reduce((sum, rec, i) => {

                      if (reviewDecisions.recommendations[String(i)] === 'accepted') {

                        return sum + (rec.source_tc_ids || []).length;

                      }

                      return sum;

                    }, 0)}

                  </div>

                  <div className="ro-metric-label">Optimised TCs</div>

                </div>

                <div className="ro-metric-card ro-metric-blue">

                  <div className="ro-metric-value">{recCounts.accepted + removalCounts.accepted} / {removed.length + recommendations.length}</div>

                  <div className="ro-metric-label">Accepted</div>

                </div>

                <div className="ro-metric-card ro-metric-violet">

                  <div className="ro-metric-value">

                    {(() => {

                      let steps = 0;

                      const totalSteps = fileMetrics?.totalSteps || summary.original_step_count || 1;

                      recommendations.forEach((rec, i) => {

                        if (reviewDecisions.recommendations[String(i)] === 'accepted') {

                          const srcCount = (rec.source_tc_ids || []).length;

                          steps += Math.max(0, srcCount - 1) * Math.ceil(totalSteps / summary.original_count);

                        }

                      });

                      steps += removalCounts.accepted * Math.ceil(totalSteps / summary.original_count);

                      return totalSteps > 0 ? `${Math.round((steps / totalSteps) * 100)}%` : '0%';

                    })()}

                  </div>

                  <div className="ro-metric-label">Suite Optimised</div>

                </div>

                <div className="ro-metric-card ro-metric-teal">

                  <div className="ro-metric-value">

                    {(() => {

                      const total = removed.length + recommendations.length;

                      const accepted = recCounts.accepted + removalCounts.accepted;

                      return total > 0 ? Math.round((accepted / total) * 100) : 0;

                    })()}%

                  </div>

                  <div className="ro-metric-label">Acceptance Rate</div>

                </div>

              </div>

            </div>

            {/* --- Cost Savings Estimate --- */}
            <div className="ro-stats-section" style={{ marginTop: 28 }}>
              <h4 className="ro-stats-section-title" style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, color: 'var(--ro-text-primary)' }}>
                Cost Savings Estimate
                <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--ro-text-muted)', marginLeft: 8 }}>-- based on accepted optimizations</span>
              </h4>
              {(() => {
                const apiSignals = /\b(api|endpoint|status.?code|[1-5]\d{2}\s|200\b|201\b|204\b|400\b|401\b|403\b|404\b|500\b|502\b|unauthorized|forbidden|not.?found|created|bad.?request|internal.?server|rest\b|graphql|microservice|payload|json|xml|header|bearer|token|curl|postman|swagger)\b/gi;
                const uiSignals = /\b(page|screen|button|click|navigate|display|field|dropdown|modal|popup|checkbox|tab|menu|form|dialog|tooltip|hover|scroll|drag|select|radio|toggle|input|textarea|label|link|icon|table|grid|panel|sidebar|header|footer|banner|card|login\s*page|approval\s*page|dashboard|verify.*visible|verify.*displayed|should.*see|should.*appear|ui|user\s*interface|browser|dom|element)\b/gi;
                const classifyTc = (text) => {
                  if (!text) return 'ui';
                  const apiHits = (text.match(apiSignals) || []).length;
                  const uiHits = (text.match(uiSignals) || []).length;
                  return apiHits > uiHits ? 'api' : 'ui';
                };
                let apiTcsOptimized = 0;
                let uiTcsOptimized = 0;
                removed.forEach(r => {
                  const tcId = String(r[idColumn] || '').trim();
                  if (reviewDecisions.removals[tcId] === 'accepted' || reviewDecisions.removals[tcId] === 'accepted-swap') {
                    const allText = Object.values(r).filter(v => typeof v === 'string').join(' ');
                    if (classifyTc(allText) === 'api') apiTcsOptimized++;
                    else uiTcsOptimized++;
                  }
                });
                recommendations.forEach((rec, i) => {
                  if (reviewDecisions.recommendations[String(i)] === 'accepted') {
                    const reducedCount = Math.max(0, (rec.source_tc_ids || []).length - 1);
                    const allText = [rec.reason, rec.intent?.feature, rec.intent?.action, ...(rec.merged_steps || [])].filter(Boolean).join(' ');
                    if (classifyTc(allText) === 'api') apiTcsOptimized += reducedCount;
                    else uiTcsOptimized += reducedCount;
                  }
                });
                const apiHoursSaved = (apiTcsOptimized / 15) * 8;
                const uiHoursSaved = (uiTcsOptimized / 10) * 8;
                const totalHoursSaved = apiHoursSaved + uiHoursSaved;
                const dollarSavings = totalHoursSaved * 23;
                return (
                  <div className="ro-metrics-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <div className="ro-metric-card" style={{ background: 'linear-gradient(135deg, rgba(249,115,22,0.06), rgba(249,115,22,0.02))', border: '1px solid rgba(249,115,22,0.15)', textAlign: 'center', position: 'relative' }}
                    >
                      <div className="ro-metric-value" style={{ color: '#f97316', fontSize: 32 }}>{totalHoursSaved.toFixed(1)}<span style={{ fontSize: 14, fontWeight: 600 }}> hrs</span></div>
                      <div className="ro-metric-label" style={{ color: '#f97316' }}>Total Hours Saved</div>
                      <div className="ro-metric-sub" style={{ color: '#f97316' }}>per execution cycle</div>
                      <div className="ro-stage-tooltip" style={{ bottom: 'auto', top: 'calc(100% + 8px)', width: 280 }}>Based on {apiTcsOptimized} API and {uiTcsOptimized} UI test cases optimized. API capacity: 15 TCs/day, UI capacity: 10 TCs/day (8-hour workday).</div>
                    </div>
                    <div className="ro-metric-card" style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.08), rgba(34,197,94,0.02))', border: '1px solid rgba(34,197,94,0.2)', textAlign: 'center', position: 'relative' }}
                    >
                      <div className="ro-metric-value" style={{ color: '#16a34a', fontSize: 32 }}>{'$'}{dollarSavings.toFixed(0)}</div>
                      <div className="ro-metric-label" style={{ color: '#16a34a' }}>Dollar Savings</div>
                      <div className="ro-metric-sub" style={{ color: '#16a34a' }}>per execution cycle</div>
                      <div className="ro-stage-tooltip" style={{ bottom: 'auto', top: 'calc(100% + 8px)', width: 280 }}>{totalHoursSaved.toFixed(1)} hours of manual effort saved, valued at the per-hour billing rate of an offshore QA resource.</div>
                    </div>
                  </div>
                );
              })()}
            </div>

            </div> {/* End Metrics Dashboard Card */}

            {/* --- Actions Card --- */}
            <div className="ro-glass-card" style={{ marginTop: 24, padding: '24px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button
                  className="ro-confirm-btn"
                  disabled={isOptimizing || !sessionId}
                  onClick={handleConfirmClick}
                >
                  {isDownloading ? <span className="ro-spinner"></span> : isOptimizing ? <span className="ro-spinner"></span> : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7,10 12,15 17,10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                  )}
                  <span>{isDownloading ? 'Generating & Downloading...' : 'Confirm & Download Optimized Excel'}</span>
                </button>
                <button
                  disabled
                  title="Push optimized suite to Azure DevOps (coming soon)"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 20px', fontSize: 14, fontWeight: 600, borderRadius: 'var(--ro-radius-md, 10px)', border: '1px solid #d1d5db', background: '#f9fafb', color: '#9ca3af', cursor: 'not-allowed', opacity: 0.7 }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16,6 12,2 8,6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
                  Push to ADO
                </button>
              </div>
              <button className="ro-reset-btn" onClick={resetAll} style={{ margin: 0 }}>{'↺'} Start Over</button>
            </div>

          </section>

        )}

      </div>



      {/* --- Training References Modal --- */}
      {showTrainingFiles && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, backdropFilter: 'blur(2px)' }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: '28px 32px', maxWidth: 500, width: '90%', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1e293b' }}>Training Reference Materials</h3>
              <button onClick={() => setShowTrainingFiles(false)} style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 16, color: '#64748b' }}>
                {'\u2715'}
              </button>
            </div>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: '#64748b' }}>
              Files uploaded during this session as domain training context:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {sessionUploadedFiles.map((name, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f8fafc' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0097A9" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{name}</span>
                </div>
              ))}
              {sessionUploadedFiles.length === 0 && (
                <p style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: 16 }}>No files were uploaded during this session.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* --- View Original Suite Modal --- */}
      {showOriginalFile && originalData && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, backdropFilter: 'blur(3px)' }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: '28px 32px', maxWidth: 900, width: '95%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1e293b' }}>Original Test Suite</h3>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>{file?.name || 'Uploaded test suite'} - {originalData?.rows?.length || 0} rows</p>
              </div>
              <button onClick={() => setShowOriginalFile(false)} style={{ width: 36, height: 36, borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 18, color: '#64748b' }}>
                x
              </button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 10, background: '#fafbfc' }}>
              {originalData?.rows?.map((row, idx) => {
                const idCol = originalData.id_column || originalData.tc_id_field;
                const tcId = idCol ? String(row[idCol] || '').trim() : '';
                const isHeader = tcId && tcId !== String(originalData.rows[idx - 1]?.[idCol] || '').trim();
                return (
                  <div key={idx} style={{ padding: '10px 16px', borderBottom: '1px solid #f1f5f9', background: isHeader ? '#f8fafc' : '#fff' }}>
                    {isHeader && (
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>
                        {tcId && <span style={{ color: 'var(--ro-accent-primary)', marginRight: 8 }}>{tcId}</span>}
                        {row['Title'] || row['title'] || ''}
                      </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      {(row['Step Action'] || row['step action'] || row['Steps'] || row['steps']) && (
                        <div style={{ fontSize: 12, color: '#475569' }}>
                          <span style={{ fontWeight: 600, color: '#64748b', fontSize: 10, textTransform: 'uppercase' }}>Step: </span>
                          {row['Step Action'] || row['step action'] || row['Steps'] || row['steps']}
                        </div>
                      )}
                      {(row['Step Expected'] || row['step expected'] || row['Expected Result'] || row['expected']) && (
                        <div style={{ fontSize: 12, color: '#475569' }}>
                          <span style={{ fontWeight: 600, color: '#64748b', fontSize: 10, textTransform: 'uppercase' }}>Expected: </span>
                          {row['Step Expected'] || row['step expected'] || row['Expected Result'] || row['expected']}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Toast */}

      <Toast message={toast.message} type={toast.type} visible={toast.visible} />

      </>
      )}

    </div>

  );

};



export default RegressionOptimizer;

